var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useContext, useEffect, useRef, useState } from 'react';
import { nmpAnimationTicker } from '../helper/nmpAnimationTicker';
import { t } from 'i18next';
import { DataContext } from '../player/DataContext';
import ButtonComponent from '../components/ButtonComponent';
import { selectTool, bringManipulativesToFront } from '../helper/helper';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { announce, announceAssertive } from '../helper/announcer';
import { snapToBackgroundGrid, solveNmpPieceSnap } from '../helper/snapHelper';
import { playAudio } from '../helper/audioHelper';
import globalStore from '../store';
import { v4 as uuidv4 } from 'uuid';
import { NUMBER_COLORS, DENOMINATION_KEYS, CURRENCY_IMAGES, GRID_SIZE, PIECE_GRID, PIECE_DIMENSIONS, MONEY_PIECE_DIMENSIONS, COIN_RENDER_DIMENSIONS as COIN_DIMENSIONS, COIN_EXCHANGE_LAYOUT, MONEY_PIECE_EXCHANGE_LAYOUT, MONEY_GRIDS, DENOM_BTN_MAP, } from '../constants/nmpConstants';
import '../styles/widgets/numberAndMoneyPiece.scss';
import SelectFocusOverlay from '../components/SelectFocusOverlay';
import { useDuplicateAnnounce } from '../helper/hooks/useDuplicateAnnounce';
// Reliable touch-primary detection — works on modern iPads (desktop UA)
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var NumberAndMoneyPieceManipulative = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    var data = _a.data, _u = _a.isGroup, isGroup = _u === void 0 ? false : _u;
    var _v = useContext(DataContext), selectedTool = _v.selectedTool, setSelectedTool = _v.setSelectedTool, setManipulative = _v.setManipulative, manipulative = _v.manipulative, setActiveManipulator = _v.setActiveManipulator, setManiplulatorComponentVisible = _v.setManiplulatorComponentVisible, setSelectedManipulativeId = _v.setSelectedManipulativeId, panningEnable = _v.panningEnable, setContextMenu = _v.setContextMenu;
    var _w = globalStore.useStoreState(function (store) { return store.playerStore; }), zoomLevel = _w.zoomLevel, playerBgPattern = _w.playerBgPattern, canvasWidth = _w.canvasWidth, canvasHeight = _w.canvasHeight, nmpAnimationEnabled = _w.nmpAnimationEnabled;
    var setNmpAnimationEnabled = globalStore.useStoreActions(function (store) { return store.playerStore.setNmpAnimationEnabled; });
    var CANVAS_MARGIN = 50;
    var clampCenter = function (cx, cy, w, h) {
        // Defensive: a NaN input would propagate to NaN output, which React
        // renders as `style.left: NaN` — invalid CSS, so the DOM omits the
        // property entirely and the element renders without a `left` value
        // (visible in inspect panel but not on screen). Return a safe
        // canvas-center fallback so the piece stays visible and the user can
        // recover by dragging it.
        var safeCx = Number.isFinite(cx) ? cx : canvasWidth / 2;
        var safeCy = Number.isFinite(cy) ? cy : canvasHeight / 2;
        return {
            x: Math.max(CANVAS_MARGIN + w / 2, Math.min(safeCx, canvasWidth - w / 2 - CANVAS_MARGIN)),
            y: Math.max(CANVAS_MARGIN + h / 2, Math.min(safeCy, canvasHeight - h / 2 - CANVAS_MARGIN)),
        };
    };
    var id = data.id;
    var isFocused = selectedTool.includes(id) || (!!data.groupId && selectedTool.includes(data.groupId));
    // Brief aria-label override that announces "<name> is duplicated successfully"
    // when this piece is freshly duplicated via the context-menu Copy action.
    // GameController's duplicate handler calls markAsDuplicated(newId) before the
    // mount, and this hook reads the pending set on first render so the SR hears
    // the duplication outcome instead of the regular focus label.
    var duplicateName = data.type === 'moneyPiece' ? t('text.moneyPiece') : t('text.numberPiece');
    var duplicateLabel = useDuplicateAnnounce(id, duplicateName);
    var positionLock = (_c = (_b = data.lock) === null || _b === void 0 ? void 0 : _b.positioning) !== null && _c !== void 0 ? _c : false;
    var pieceScale = (_d = data.scale) !== null && _d !== void 0 ? _d : 1;
    var isCoin = data.type === 'moneyPiece' && data.pieceType === 'coins';
    var _x = useState({ x: data.center.x, y: data.center.y }), position = _x[0], setPosition = _x[1];
    var _y = useState(false), isDragging = _y[0], setIsDragging = _y[1];
    var _z = useState(null), dragStart = _z[0], setDragStart = _z[1];
    var dragStartRef = useRef(null);
    var hasMoved = useRef(false);
    var dragStated = useRef(false);
    var dragStartItem = useRef(null);
    var posRef = useRef(position);
    posRef.current = position;
    var isDraggingRef = useRef(false);
    var selectedToolRef = useRef(selectedTool);
    selectedToolRef.current = selectedTool;
    // ids that should be brought to the front of the `manipulative` array on
    // drop (captured at mousedown; the actual array reorder is deferred to
    // drop time in stopDrag's getReorderStateChanges, so a plain click without
    // movement still commits the bring-to-front, matching the prior zIndex
    // behavior — unlike other manipulatives, NMP intentionally reorders on a
    // bare click too).
    var topIdsRef = useRef(null);
    // Guards against selectTool being applied twice for the same physical click: stopDrag's
    // onUp (below) calls it eagerly since a bring-to-front reorder can move this piece's DOM
    // node before the native `click` event fires, causing the browser to drop that click
    // entirely — but when it DOES still fire, ButtonComponent's `clickHandler` calls selectTool
    // again with the same id/modifiers. For a plain click that's a harmless no-op, but for a
    // multi-select (ctrl/shift) click selectTool TOGGLES — a second call immediately un-selects
    // what the first call just selected, silently dropping the group from selectedTool right
    // after adding it.
    var selectionAppliedRef = useRef(false);
    var groupDragSnapshotRef = useRef({});
    // Starting left/top of the group's dashed selection-outline box (rendered by
    // NumberAndMoneyPieceSelectionGroup), captured at drag-start so it can be
    // nudged by the same inline-style deltas as the member pieces below.
    var groupBoxStartRef = useRef(null);
    var multiSelectStartPositions = useRef([]);
    var multiSelectDragStartCenter = useRef({ x: 0, y: 0 });
    // True when this piece's entire snap cluster is part of the current multi-select
    // (so the drag moves the whole cluster rigidly — nothing is actually being
    // separated). Set at mousedown, consumed by both the drag-start "leave
    // cluster" logic and the drop-time snappedId recompute, mirroring how
    // isGroupDrag is already exempted from both of those paths.
    var isFullClusterSelectedRef = useRef(false);
    var wrapperRef = useRef(null);
    var manipulativeRef = useRef(manipulative);
    manipulativeRef.current = manipulative;
    var centerRef = useRef({ x: data.center.x, y: data.center.y });
    centerRef.current = { x: data.center.x, y: data.center.y };
    var _0 = useState(false), flipAnimating = _0[0], setFlipAnimating = _0[1];
    // ─── Keyboard accessibility state (mirrors LinkingCubes pattern) ─────
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    var _1 = useState(''), announceText = _1[0], setAnnounceText = _1[1];
    var _2 = useState(false), isKeyboardFocused = _2[0], setIsKeyboardFocused = _2[1];
    // Break animation phase detection — read ticker at mount time (not data.animating)
    var shouldFadeIn = useState(function () { return nmpAnimationTicker.isAnimating; })[0];
    var _3 = useState(function () { return (shouldFadeIn ? 'in' : null); }), breakPhase = _3[0], setBreakPhase = _3[1];
    var _4 = useState(function () { return (shouldFadeIn ? 0 : 1); }), fadeInOpacity = _4[0], setFadeInOpacity = _4[1];
    var _5 = useState(1), fadeOutOpacity = _5[0], setFadeOutOpacity = _5[1];
    // Lazy migration: older saved states for standalone tens may have
    // `rotation: 90/270` with `defaultDimensions` still stored vertically.
    // Normalize on mount by swapping dims + zeroing rotation so the piece
    // renders natively as a horizontal row (avoids sub-pixel drift from
    // CSS-rotating a bordered flex column). Guarded with a ref so it runs
    // at most once per piece.
    var migrationRanRef = useRef(false);
    useEffect(function () {
        if (migrationRanRef.current)
            return;
        if (data.type !== 'numberPiece')
            return;
        if (data.denomination !== 'tens')
            return;
        if (data.groupId)
            return; // grouped pieces already use the swap path
        var rot = (((data.rotation || 0) % 360) + 360) % 360;
        var needsMigration = rot === 90 || rot === 270;
        if (!needsMigration)
            return;
        var dd = data.defaultDimensions;
        // Only migrate if dims are still stored in the pre-rotation (vertical)
        // orientation — height > width. If they've already been swapped we
        // leave them alone.
        if (!dd || dd.width >= dd.height)
            return;
        migrationRanRef.current = true;
        var newDefault = { width: dd.height, height: dd.width };
        var newCurrent = data.dimensions
            ? { width: data.dimensions.height, height: data.dimensions.width }
            : newDefault;
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === id
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { defaultDimensions: newDefault, dimensions: newCurrent, rotation: 0 }) }) : item;
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Trigger fade-out when animatingOut is set
    useEffect(function () {
        if (data.animatingOut) {
            requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                    setFadeOutOpacity(0);
                });
            });
        }
    }, [data.animatingOut]);
    // Trigger fade-in transition after mount
    useEffect(function () {
        if (breakPhase === 'in' && fadeInOpacity === 0) {
            requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                    setFadeInOpacity(1);
                });
            });
        }
    }, [breakPhase, fadeInOpacity]);
    // Self-heal a corrupted center on render. Pieces that came through an
    // earlier buggy code path (e.g. an exchange whose source had a NaN
    // coordinate) end up persisted with `center.x` or `center.y` equal to
    // null / NaN — JSON.stringify turns NaN into null, so a saved-state
    // round-trip or even a single bad drop leaves the piece with one axis
    // missing. React renders the missing axis as `style.left/top: undefined`,
    // which is invalid CSS, so the DOM omits the property and the coin is
    // visible in the inspector but not on screen. Replace with canvas
    // center so the piece becomes visible and the user can drag it; the
    // drop-time guards then keep it finite from there.
    var centerNeedsRepair = !Number.isFinite((_e = data.center) === null || _e === void 0 ? void 0 : _e.x) || !Number.isFinite((_f = data.center) === null || _f === void 0 ? void 0 : _f.y);
    useEffect(function () {
        if (!centerNeedsRepair)
            return;
        setManipulative(function (prev) {
            return prev.map(function (item) {
                if (item.data.id !== id)
                    return item;
                var c = item.data.center;
                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: {
                            x: Number.isFinite(c === null || c === void 0 ? void 0 : c.x) ? c.x : canvasWidth / 2,
                            y: Number.isFinite(c === null || c === void 0 ? void 0 : c.y) ? c.y : canvasHeight / 2,
                        } }) });
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [centerNeedsRepair]);
    // Sync position from data (skip during drag to avoid fighting local drag state)
    useEffect(function () {
        if (!isDraggingRef.current) {
            setPosition({ x: data.center.x, y: data.center.y });
        }
    }, [data.center.x, data.center.y]);
    // Snap helpers — compute grid-aligned dimensions from denomination (not data.dimensions)
    var getSnapDims = function (d) {
        var _a;
        var w, h;
        if (d.type === 'numberPiece') {
            var np = d;
            var dims = PIECE_DIMENSIONS[np.denomination];
            w = dims.width;
            h = dims.height;
        }
        else if (d.type === 'moneyPiece') {
            var mp = d;
            if (mp.pieceType !== 'coins') {
                var dims = MONEY_PIECE_DIMENSIONS[mp.denomination];
                w = dims.width;
                h = dims.height;
            }
            else {
                var coinDim = COIN_DIMENSIONS[mp.denomination];
                w = coinDim.width;
                h = coinDim.height;
            }
        }
        else {
            w = PIECE_DIMENSIONS.ones.width;
            h = PIECE_DIMENSIONS.ones.height;
        }
        // Detect rotation via CSS rotation OR via swapped defaultDimensions (grouped rotation path)
        var rot = (d.rotation || 0) % 360;
        var rotIsRotated = Math.abs(rot - 90) < 1 || Math.abs(rot - 270) < 1;
        var dimsIsRotated = d.defaultDimensions
            ? d.defaultDimensions.width > d.defaultDimensions.height
            : false;
        var isRotated = rotIsRotated || dimsIsRotated;
        var scale = (_a = d.scale) !== null && _a !== void 0 ? _a : 1;
        var finalW = isRotated ? h : w;
        var finalH = isRotated ? w : h;
        return { w: finalW * scale, h: finalH * scale };
    };
    // Returns base dimensions without scale (used for cluster repositioning after scale changes)
    var getBaseDims = function (d) {
        var w, h;
        if (d.type === 'numberPiece') {
            var np = d;
            var dims = PIECE_DIMENSIONS[np.denomination];
            w = dims.width;
            h = dims.height;
        }
        else if (d.type === 'moneyPiece') {
            var mp = d;
            if (mp.pieceType !== 'coins') {
                var dims = MONEY_PIECE_DIMENSIONS[mp.denomination];
                w = dims.width;
                h = dims.height;
            }
            else {
                var coinDim = COIN_DIMENSIONS[mp.denomination];
                w = coinDim.width;
                h = coinDim.height;
            }
        }
        else {
            w = PIECE_DIMENSIONS.ones.width;
            h = PIECE_DIMENSIONS.ones.height;
        }
        var rot = (d.rotation || 0) % 360;
        var rotIsRotated = Math.abs(rot - 90) < 1 || Math.abs(rot - 270) < 1;
        var dimsIsRotated = d.defaultDimensions
            ? d.defaultDimensions.width > d.defaultDimensions.height
            : false;
        var isRotated = rotIsRotated || dimsIsRotated;
        return { w: isRotated ? h : w, h: isRotated ? w : h };
    };
    var snapNMPPosition = function (cx, cy, w, h, excludeIds) {
        var _a;
        // Coins: snap top-left to grid when ON, no snap when OFF
        if (isCoin) {
            if (playerBgPattern === 'square') {
                var snappedTL = snapToBackgroundGrid(cx - w / 2, cy - h / 2, PIECE_GRID);
                return { x: snappedTL.x + w / 2, y: snappedTL.y + h / 2 };
            }
            return { x: cx, y: cy };
        }
        var topLeftX = cx - w / 2;
        var topLeftY = cy - h / 2;
        // Other-piece list — used by both the grid-on anti-stacking path and
        // the grid-off snap-to-piece path.
        var others = manipulative.filter(function (m) {
            return (m.data.type === 'numberPiece' || m.data.type === 'moneyPiece') &&
                m.data.id !== id &&
                !(data.groupId && m.data.groupId === data.groupId) &&
                !(excludeIds && excludeIds.has(m.data.id));
        });
        if (playerBgPattern === 'square') {
            // Snap the top-left to the global background grid. Pieces stay grid-to-grid
            // aligned and may freely overlap one another — including on another piece's
            // internal grid lines. (Overlapping pieces are no longer pushed out to an
            // adjacent edge.)
            var snappedTL = snapToBackgroundGrid(topLeftX, topLeftY, PIECE_GRID);
            return { x: snappedTL.x + w / 2, y: snappedTL.y + h / 2 };
        }
        // Grid OFF: snap to the nearest NMP piece, aligned to that piece's grid.
        // Center-based classification (overlap / flush-adjacent / no snap) lives in
        // solveNmpPieceSnap so it can be unit-tested independently of the widget.
        return solveNmpPieceSnap(cx, cy, w, h, others.map(function (other) {
            var _a;
            var od = other.data;
            var _b = getSnapDims(od), ow = _b.w, oh = _b.h;
            return {
                tlX: od.center.x - ow / 2,
                tlY: od.center.y - oh / 2,
                w: ow,
                h: oh,
                scale: (_a = od.scale) !== null && _a !== void 0 ? _a : 1,
            };
        }), (_a = data.scale) !== null && _a !== void 0 ? _a : 1);
    };
    // Group-aware snap: only background grid snap, no snap-to-piece (preserves formation)
    var snapNMPGroupPosition = function (cx, cy, w, h) {
        if (isCoin) {
            if (playerBgPattern === 'square') {
                var snappedTL = snapToBackgroundGrid(cx - w / 2, cy - h / 2, PIECE_GRID);
                return { x: snappedTL.x + w / 2, y: snappedTL.y + h / 2 };
            }
            return { x: cx, y: cy };
        }
        if (playerBgPattern === 'square') {
            var snappedTL = snapToBackgroundGrid(cx - w / 2, cy - h / 2, PIECE_GRID);
            return { x: snappedTL.x + w / 2, y: snappedTL.y + h / 2 };
        }
        return { x: cx, y: cy };
    };
    // ─── Keyboard a11y helpers (label + handler) ─────────────────────────
    var buildPieceLabel = function () {
        var _a, _b, _c, _d, _e, _f;
        var denomName = t("text.".concat(data.denomination)) || data.denomination;
        var typeLabel = data.type === 'moneyPiece'
            ? data.pieceType === 'coins'
                ? t('text.moneyPieceCoinLabel', { denom: denomName })
                : t('text.moneyPieceLabel', { denom: denomName })
            : t('text.numberPieceLabel', { denom: denomName });
        var flippedSuffix = data.type === 'moneyPiece' && data.isFlipped ? ", ".concat(t('text.flipped')) : '';
        var rotatedSuffix = data.type === 'numberPiece' &&
            data.denomination === 'tens' &&
            ((_b = (_a = data.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 0) > ((_d = (_c = data.defaultDimensions) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 0)
            ? ", ".concat(t('text.rotated'))
            : '';
        // State suffixes — these let screen readers describe the piece's current
        // affordances without the user having to inspect the toolbar.
        var selectedSuffix = isFocused ? ", ".concat(t('text.selected')) : '';
        var positionLockSuffix = ((_e = data.lock) === null || _e === void 0 ? void 0 : _e.positioning) ? ", ".concat(t('text.positionLocked')) : '';
        var editingLockSuffix = ((_f = data.lock) === null || _f === void 0 ? void 0 : _f.editing) ? ", ".concat(t('text.editingLocked')) : '';
        // Snap-cluster membership: only announce when there's at least one peer
        // sharing the snappedId (a stale singleton cluster shouldn't count).
        var inCluster = !!data.snappedId &&
            manipulative.some(function (m) {
                return m.data.id !== id &&
                    (m.data.type === 'numberPiece' || m.data.type === 'moneyPiece') &&
                    m.data.snappedId === data.snappedId;
            });
        var clusterSuffix = inCluster ? ", ".concat(t('text.snappedGroup')) : '';
        return "".concat(typeLabel).concat(flippedSuffix).concat(rotatedSuffix).concat(selectedSuffix).concat(positionLockSuffix).concat(editingLockSuffix).concat(clusterSuffix);
    };
    var handleKeyDown = function (e) {
        var _a, _b, _c;
        if (isGroup || panningEnable)
            return;
        // Enter / Space / Shift+F10 / ContextMenu — open context menu
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            e.stopPropagation();
            if (!selectedTool.includes(id))
                setSelectedTool([id]);
            var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
            }
            return;
        }
        // Delete / Backspace — remove this piece
        if ((e.key === 'Delete' || e.key === 'Backspace') && !((_b = data.lock) === null || _b === void 0 ? void 0 : _b.editing)) {
            e.preventDefault();
            var beforeSnapshot = JSON.stringify(manipulativeRef.current);
            var nextItems_1 = manipulativeRef.current.filter(function (m) { return m.data.id !== id; });
            saveState({
                type: 'multipleManipulatives',
                id: id,
                propertyName: 'multipleCompleteDataDelete',
                old: beforeSnapshot,
                new: JSON.stringify(nextItems_1),
            });
            setManipulative(function () { return nextItems_1; });
            setSelectedTool(selectedTool.filter(function (s) { return s !== id; }));
            announce(t('text.pieceRemoved', { name: buildPieceLabel() }));
            return;
        }
        // Arrow keys — move piece (and its snap cluster) by 10/50 px, zoom-scaled
        var isArrow = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key);
        if (!isArrow)
            return;
        if (positionLock) {
            e.preventDefault();
            return;
        }
        e.preventDefault();
        var step = (e.shiftKey ? 50 : 10) / (zoomLevel || 1);
        var deltaMap = {
            ArrowUp: { x: 0, y: -step },
            ArrowDown: { x: 0, y: step },
            ArrowLeft: { x: -step, y: 0 },
            ArrowRight: { x: step, y: 0 },
        };
        var delta = deltaMap[e.key];
        var curPos = (_c = keyMoveCurPosRef.current) !== null && _c !== void 0 ? _c : { x: data.center.x, y: data.center.y };
        if (!keyMoveStartRef.current)
            keyMoveStartRef.current = __assign({}, curPos);
        var newCenter = { x: curPos.x + delta.x, y: curPos.y + delta.y };
        keyMoveCurPosRef.current = newCenter;
        // Translate this piece + all snap-cluster members by the same delta
        var snappedId = data.snappedId;
        setManipulative(function (prev) {
            return prev.map(function (m) {
                var md = m.data;
                if (md.id === id) {
                    return __assign(__assign({}, m), { data: __assign(__assign({}, md), { center: newCenter }) });
                }
                if (snappedId && md.snappedId === snappedId && md.id !== id) {
                    return __assign(__assign({}, m), { data: __assign(__assign({}, md), { center: { x: md.center.x + delta.x, y: md.center.y + delta.y } }) });
                }
                return m;
            });
        });
        // Debounced save + announce 500ms after last keypress
        if (keyMoveSaveTimer.current)
            clearTimeout(keyMoveSaveTimer.current);
        keyMoveSaveTimer.current = setTimeout(function () {
            var oldPos = keyMoveStartRef.current;
            var newPos = keyMoveCurPosRef.current;
            if (!oldPos || !newPos)
                return;
            saveState({
                type: id,
                id: id,
                propertyName: 'center',
                old: JSON.stringify(oldPos),
                new: JSON.stringify(newPos),
            });
            var dx = Math.round(newPos.x - oldPos.x);
            var dy = Math.round(newPos.y - oldPos.y);
            var directions = [];
            if (dy < 0)
                directions.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
            if (dy > 0)
                directions.push("".concat(t('text.down'), " ").concat(dy));
            if (dx < 0)
                directions.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
            if (dx > 0)
                directions.push("".concat(t('text.right'), " ").concat(dx));
            setAnnounceText('');
            requestAnimationFrame(function () {
                return setAnnounceText(t('text.manipulativeMoved', {
                    name: buildPieceLabel(),
                    direction: directions.join(" ".concat(t('text.and'), " ")),
                    x: Math.round(newPos.x),
                    y: Math.round(newPos.y),
                }));
            });
            keyMoveStartRef.current = null;
            keyMoveCurPosRef.current = null;
        }, 500);
    };
    // Drag handling
    var onMouseDown = function (e) {
        if (positionLock)
            return;
        if ('touches' in e)
            e.preventDefault();
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        if (!panningEnable) {
            dragStartItem.current = __assign({}, position);
            dragStartRef.current = { x: clientX, y: clientY };
            hasMoved.current = false;
            // Read from the always-fresh ref instead of the closure prop. If the
            // user just performed a Group action, the closure may not have caught
            // the latest manipulative state — building the snapshot from a stale
            // closure produced a partial snapshot (only 1 of 4 grouped nickels)
            // and at drop the 3 unsnapshotted members stayed at their original
            // centers (looked like they "vanished" from where the group landed).
            // Capture group snapshot and determine which IDs should move to the
            // front of the `manipulative` array.
            var topIds = void 0;
            // Whether THIS piece's own group is itself part of the broader selection (its
            // groupId literally present in selectedTool) AND the resolved multi-select path
            // will actually engage (requires length > 1 — matches isInMultiSelect below,
            // which dispatches multiSelectMoveStart). Checking groupId membership alone
            // wrongly deferred to "the multi-select path covers it" even when selectedTool
            // was JUST [groupId] (only the group selected, nothing else) — in that case
            // isInMultiSelect never fires (length is 1), so nothing moved the siblings at
            // all: this skipped the whole-group snapshot AND the multi-select path never
            // ran. A piece's own group must always drag together unless the group is
            // ACTUALLY being moved via that other path (group + something else selected).
            var groupCoveredByMultiSelect = !!data.groupId && selectedTool.includes(data.groupId) && selectedTool.length > 1;
            if (groupCoveredByMultiSelect) {
                var resolvedIds = new Set(selectedTool.flatMap(function (sid) {
                    return sid.startsWith('manipulativeGroup_')
                        ? manipulativeRef.current
                            .filter(function (m) { return m.data.groupId === sid; })
                            .map(function (m) { return m.data.id; })
                        : [sid];
                }));
                topIds = resolvedIds;
                groupDragSnapshotRef.current = {};
            }
            else if (data.groupId) {
                var members = manipulativeRef.current.filter(function (m) { return m.data.groupId === data.groupId; });
                groupDragSnapshotRef.current = Object.fromEntries(members.map(function (m) { return [m.data.id, __assign({}, m.data.center)]; }));
                topIds = new Set(members.map(function (m) { return m.data.id; }));
                var boxEl = document.querySelector("[data-manipulative-id=\"".concat(data.groupId, "\"]"));
                groupBoxStartRef.current = boxEl
                    ? { x: parseFloat(boxEl.style.left) || 0, y: parseFloat(boxEl.style.top) || 0 }
                    : null;
            }
            else {
                groupDragSnapshotRef.current = {};
                groupBoxStartRef.current = null;
                topIds = new Set([id]);
            }
            // The actual array reorder is deferred to drop time (stopDrag's
            // getReorderStateChanges) — only remember which ids should move to
            // front here. The transient `isDragging` CSS boost (below) provides
            // the immediate visual lift while grabbed.
            topIdsRef.current = topIds;
            setDragStart({
                x: clientX - position.x * zoomLevel,
                y: clientY - position.y * zoomLevel,
            });
            dragStated.current = true;
            isDraggingRef.current = true;
            // Multi-select: capture start positions of all selected NMP pieces (including group members)
            var isInMultiSelect = selectedTool.length > 1 &&
                (selectedTool.includes(id) || (!!data.groupId && selectedTool.includes(data.groupId)));
            if (isInMultiSelect) {
                multiSelectDragStartCenter.current = { x: data.center.x, y: data.center.y };
                // Expand any group IDs in selectedTool to their member piece IDs
                var resolvedMultiIds_1 = new Set(selectedTool.flatMap(function (sid) {
                    return sid.startsWith('manipulativeGroup_')
                        ? manipulativeRef.current
                            .filter(function (m) { return m.data.groupId === sid; })
                            .map(function (m) { return m.data.id; })
                        : [sid];
                }));
                multiSelectStartPositions.current = manipulativeRef.current
                    .filter(function (m) {
                    return resolvedMultiIds_1.has(m.data.id) &&
                        (m.data.type === 'numberPiece' || m.data.type === 'moneyPiece');
                })
                    .map(function (m) { return ({
                    id: m.data.id,
                    center: __assign({}, m.data.center),
                }); });
                // Is this piece's whole snap cluster part of the selection? If so, the
                // drag moves the cluster rigidly and nothing should be un-snapped.
                var startSnappedId_1 = data.snappedId;
                var clusterMateIds = startSnappedId_1
                    ? manipulativeRef.current
                        .filter(function (m) { return m.data.snappedId === startSnappedId_1; })
                        .map(function (m) { return m.data.id; })
                    : [];
                isFullClusterSelectedRef.current =
                    !!startSnappedId_1 &&
                        clusterMateIds.length > 1 &&
                        clusterMateIds.every(function (mid) { return mid === id || resolvedMultiIds_1.has(mid); });
                customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: id });
            }
            else {
                isFullClusterSelectedRef.current = false;
            }
        }
    };
    useEffect(function () {
        var handleMove = function (e) {
            var _a, _b, _c, _d, _e, _f;
            if ('touches' in e)
                e.preventDefault();
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            if (!dragStartRef.current)
                return;
            var dx = clientX - dragStartRef.current.x;
            var dy = clientY - dragStartRef.current.y;
            if (!hasMoved.current && Math.sqrt(dx * dx + dy * dy) > 5 && !positionLock) {
                hasMoved.current = true;
                setIsDragging(true);
                // Leave the snap cluster when drag starts (snappedId re-assigned on drop if re-snapped).
                // Skip this for group drags: the group-drop path never recomputes snappedId (it only
                // applies grid snap, not snap-to-piece), and formation stays intact during a group move,
                // so clearing it here would permanently drop the snapped outline with no way to restore it.
                var isGroupDrag = !!data.groupId && Object.keys(groupDragSnapshotRef.current).length > 0;
                var currentSnappedId_1 = (_b = (_a = manipulativeRef.current.find(function (m) { return m.data.id === id; })) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.snappedId;
                if (currentSnappedId_1 && !isGroupDrag && !isFullClusterSelectedRef.current) {
                    // Removing this piece can split the remaining cluster members into
                    // disjoint groups (e.g. dragging the middle piece out of a 3-piece
                    // row leaves the two end pieces sharing the old snappedId even
                    // though they're no longer touching each other). Recompute
                    // connectivity among the leftover same-cluster members via BFS over
                    // the same touching/grid-aligned adjacency test used on drop, and
                    // re-split them into fresh clusters (or clear snappedId for members
                    // left alone).
                    var leftover_1 = manipulativeRef.current.filter(function (m) { return m.data.id !== id && m.data.snappedId === currentSnappedId_1; });
                    var isTouching_1 = function (a, b) {
                        var _a;
                        var ad = a.data;
                        var bd = b.data;
                        var _b = getSnapDims(ad), aw = _b.w, ah = _b.h;
                        var _c = getSnapDims(bd), bw = _c.w, bh = _c.h;
                        var aTLX = ad.center.x - aw / 2;
                        var aTLY = ad.center.y - ah / 2;
                        var bTLX = bd.center.x - bw / 2;
                        var bTLY = bd.center.y - bh / 2;
                        var gridStep = PIECE_GRID * ((_a = ad.scale) !== null && _a !== void 0 ? _a : 1);
                        var TOL = 1;
                        var hOverlap = aTLX < bTLX + bw + TOL && aTLX + aw > bTLX - TOL;
                        var vOverlap = aTLY < bTLY + bh + TOL && aTLY + ah > bTLY - TOL;
                        if (!hOverlap || !vOverlap)
                            return false;
                        // See the drop-time touching check above: hOverlap/vOverlap alone
                        // allow a corner-only diagonal touch; require genuine overlap on
                        // at least one axis so a diagonal pair is never treated as
                        // connected in the cluster graph.
                        var hRealOverlap = aTLX < bTLX + bw - TOL && aTLX + aw > bTLX + TOL;
                        var vRealOverlap = aTLY < bTLY + bh - TOL && aTLY + ah > bTLY + TOL;
                        if (!hRealOverlap && !vRealOverlap)
                            return false;
                        var dx = aTLX - bTLX;
                        var dy = aTLY - bTLY;
                        var xAligned = Math.abs(Math.round(dx / gridStep) * gridStep - dx) < TOL;
                        var yAligned = Math.abs(Math.round(dy / gridStep) * gridStep - dy) < TOL;
                        return xAligned && yAligned;
                    };
                    var idToNewCluster_1 = new Map();
                    var visited_1 = new Set();
                    leftover_1.forEach(function (member) {
                        var memberId = member.data.id;
                        if (visited_1.has(memberId))
                            return;
                        var component = [];
                        var queue = [member];
                        visited_1.add(memberId);
                        var _loop_1 = function () {
                            var cur = queue.shift();
                            component.push(cur);
                            leftover_1.forEach(function (other) {
                                var otherId = other.data.id;
                                if (!visited_1.has(otherId) && isTouching_1(cur, other)) {
                                    visited_1.add(otherId);
                                    queue.push(other);
                                }
                            });
                        };
                        while (queue.length > 0) {
                            _loop_1();
                        }
                        var newId = component.length > 1 ? uuidv4().replace(/-/g, '') : undefined;
                        component.forEach(function (m) { return idToNewCluster_1.set(m.data.id, newId); });
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.id === id)
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { snappedId: undefined }) });
                            if (idToNewCluster_1.has(item.data.id))
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { snappedId: idToNewCluster_1.get(item.data.id) }) });
                            return item;
                        });
                    });
                }
            }
            if (!hasMoved.current)
                return;
            if (positionLock)
                return;
            var rawX = (clientX - ((_c = dragStart === null || dragStart === void 0 ? void 0 : dragStart.x) !== null && _c !== void 0 ? _c : 0)) / zoomLevel;
            var rawY = (clientY - ((_d = dragStart === null || dragStart === void 0 ? void 0 : dragStart.y) !== null && _d !== void 0 ? _d : 0)) / zoomLevel;
            var hw = (data.dimensions.width * pieceScale) / 2;
            var hh = (data.dimensions.height * pieceScale) / 2;
            var newX = Math.max(hw, Math.min(rawX, canvasWidth - hw));
            var newY = Math.max(hh, Math.min(rawY, canvasHeight - hh));
            // Defensive: any NaN here (e.g. zoomLevel briefly 0 / undefined, or
            // dragStart inheriting a corrupted position) propagates through
            // posRef → setPosition → React render → DOM `style.left: NaN`,
            // which is invalid CSS so the DOM omits the property and the piece
            // becomes invisible without being removed. Walk through fallbacks
            // until we find a finite value — posRef and data.center can each be
            // poisoned from a prior corrupted drop, so end at canvas center.
            if (!Number.isFinite(newX)) {
                newX = Number.isFinite((_e = posRef.current) === null || _e === void 0 ? void 0 : _e.x)
                    ? posRef.current.x
                    : Number.isFinite(data.center.x)
                        ? data.center.x
                        : canvasWidth / 2;
            }
            if (!Number.isFinite(newY)) {
                newY = Number.isFinite((_f = posRef.current) === null || _f === void 0 ? void 0 : _f.y)
                    ? posRef.current.y
                    : Number.isFinite(data.center.y)
                        ? data.center.y
                        : canvasHeight / 2;
            }
            posRef.current = { x: newX, y: newY };
            setPosition({ x: newX, y: newY });
            // Move all other group members by the same delta (skip only when the group
            // itself — not just any unrelated extra selection — is covered by the
            // resolved-id multi-select path; see groupCoveredByMultiSelect in onMouseDown).
            var isInMultiSelectMove = selectedToolRef.current.length > 1 &&
                (selectedToolRef.current.includes(id) ||
                    (!!data.groupId && selectedToolRef.current.includes(data.groupId)));
            var groupCoveredByMultiSelectMove = !!data.groupId &&
                selectedToolRef.current.includes(data.groupId) &&
                selectedToolRef.current.length > 1;
            if (data.groupId &&
                Object.keys(groupDragSnapshotRef.current).length > 0 &&
                !groupCoveredByMultiSelectMove) {
                // Self-heal the snapshot: ensure every current group member has an
                // entry. The mousedown-time snapshot occasionally came back missing
                // members (race between Group action commit and the mousedown event
                // closure), causing those members to skip the inline-style update
                // below — they appeared "vanished" until the user dragged again
                // and the snapshot was rebuilt fresh. Backfill with the member's
                // current `data.center` (unchanged during drag since we only mutate
                // inline DOM until drop) so the delta math stays correct.
                for (var _i = 0, _g = manipulativeRef.current; _i < _g.length; _i++) {
                    var member = _g[_i];
                    if ((member.data.type !== 'numberPiece' && member.data.type !== 'moneyPiece') ||
                        member.data.groupId !== data.groupId ||
                        !member.data.id) {
                        continue;
                    }
                    if (!groupDragSnapshotRef.current[member.data.id]) {
                        groupDragSnapshotRef.current[member.data.id] = __assign({}, member.data.center);
                    }
                }
                var mySnap = groupDragSnapshotRef.current[id];
                if (mySnap) {
                    var dxCanvas_1 = newX - mySnap.x;
                    var dyCanvas_1 = newY - mySnap.y;
                    // Direct DOM updates — avoids 499 setManipulative calls per mousemove
                    document
                        .querySelectorAll("[data-nmp-id][data-group-id=\"".concat(data.groupId, "\"]"))
                        .forEach(function (el) {
                        if (el === wrapperRef.current)
                            return;
                        var memberId = el.getAttribute('data-nmp-id');
                        var snap = groupDragSnapshotRef.current[memberId];
                        if (!snap)
                            return;
                        el.style.left = "".concat(snap.x + dxCanvas_1, "px");
                        el.style.top = "".concat(snap.y + dyCanvas_1, "px");
                    });
                    if (groupBoxStartRef.current) {
                        var boxEl = document.querySelector("[data-manipulative-id=\"".concat(data.groupId, "\"]"));
                        if (boxEl) {
                            boxEl.style.left = "".concat(groupBoxStartRef.current.x + dxCanvas_1, "px");
                            boxEl.style.top = "".concat(groupBoxStartRef.current.y + dyCanvas_1, "px");
                        }
                    }
                }
            }
            // Multi-select: broadcast cumulative delta so other selected pieces follow
            if (isInMultiSelectMove) {
                customEventHandler.dispatchEvent('multiSelectMove', {
                    deltaX: newX - multiSelectDragStartCenter.current.x,
                    deltaY: newY - multiSelectDragStartCenter.current.y,
                    sourceId: id,
                });
            }
        };
        var stopDrag = function (e) {
            var _a, _b, _c, _d, _e, _f;
            if (!dragStart)
                return;
            // Commit the bring-to-front reorder captured at mousedown (topIdsRef)
            // by moving those ids to the end of the `manipulative` array — deferred
            // to drop time so a plain click (no movement) still reorders, matching
            // the prior zIndex-based behavior.
            var getReorderStateChanges = function () {
                var changes = [];
                var topIds = topIdsRef.current;
                topIdsRef.current = null;
                if (topIds && topIds.size > 0) {
                    // manipulativeRef.current is used ONLY to compute ID ORDER here —
                    // safe even if it's one render behind the position/snap update
                    // just committed above, since that update never changes array
                    // order (only per-item data). The actual reorder is applied via a
                    // functional setManipulative update against the true latest state
                    // (below), never by replacing state with objects built from this
                    // ref — doing that would silently clobber the fresh position/
                    // snappedId data just committed.
                    var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                    var orderedTopIds_1 = manipulativeRef.current
                        .filter(function (m) { return topIds.has(m.data.id); })
                        .map(function (m) { return m.data.id; });
                    var alreadyAtFront = orderedTopIds_1.length > 0 &&
                        beforeIds.slice(-orderedTopIds_1.length).join(',') === orderedTopIds_1.join(',');
                    if (orderedTopIds_1.length > 0 && !alreadyAtFront) {
                        var afterIds = __spreadArray(__spreadArray([], beforeIds.filter(function (bid) { return !topIds.has(bid); }), true), orderedTopIds_1, true);
                        setManipulative(function (prev) { return bringManipulativesToFront(prev, orderedTopIds_1); });
                        changes.push({
                            type: 'multipleManipulatives',
                            id: orderedTopIds_1.join(','),
                            propertyName: 'reorder',
                            old: JSON.stringify(beforeIds),
                            new: JSON.stringify(afterIds),
                        });
                    }
                }
                return changes;
            };
            if (hasMoved.current && !positionLock) {
                var _g = getSnapDims(data), w = _g.w, h = _g.h;
                var isInMultiSelectStop = selectedToolRef.current.length > 1 &&
                    (selectedToolRef.current.includes(id) ||
                        (!!data.groupId && selectedToolRef.current.includes(data.groupId)));
                if (data.groupId &&
                    Object.keys(groupDragSnapshotRef.current).length > 0 &&
                    !isInMultiSelectStop) {
                    // Group drop: no snap-to-piece (would shift formation); only grid snap applies
                    var groupSnapped = snapNMPGroupPosition(posRef.current.x, posRef.current.y, w, h);
                    var snapDx = groupSnapped.x - posRef.current.x;
                    var snapDy = groupSnapped.y - posRef.current.y;
                    var dragDx = posRef.current.x - groupDragSnapshotRef.current[id].x;
                    var dragDy = posRef.current.y - groupDragSnapshotRef.current[id].y;
                    // Pre-compute all final positions synchronously while the snapshot ref is still populated.
                    // (The React updater runs asynchronously — by that time the ref may already be cleared.)
                    var stateChanges = [];
                    var finalPositions_1 = new Map();
                    var _loop_2 = function (memberId, snap) {
                        var newCenter = void 0;
                        if (memberId === id) {
                            newCenter = groupSnapped;
                        }
                        else {
                            // Clamp non-dragged members so a large dragger delta against
                            // the opposite edge can't push them off-canvas — without this
                            // they get committed at off-screen coordinates and look like
                            // they vanished. Each member uses its own snap dims because
                            // a heterogeneous group (e.g. mixed denominations) can have
                            // different sizes.
                            var memberItem = manipulativeRef.current.find(function (m) { return m.data.id === memberId; });
                            var memberData = memberItem === null || memberItem === void 0 ? void 0 : memberItem.data;
                            var memberDims = memberData ? getSnapDims(memberData) : { w: w, h: h };
                            newCenter = clampCenter(snap.x + dragDx + snapDx, snap.y + dragDy + snapDy, memberDims.w, memberDims.h);
                        }
                        finalPositions_1.set(memberId, newCenter);
                        stateChanges.push({
                            type: memberId,
                            id: memberId.split('_')[1],
                            propertyName: 'center',
                            old: JSON.stringify(snap),
                            new: JSON.stringify(newCenter),
                        });
                    };
                    for (var _i = 0, _h = Object.entries(groupDragSnapshotRef.current); _i < _h.length; _i++) {
                        var _j = _h[_i], memberId = _j[0], snap = _j[1];
                        _loop_2(memberId, snap);
                    }
                    groupDragSnapshotRef.current = {};
                    groupBoxStartRef.current = null;
                    // Do NOT clear inline DOM styles before setManipulative. React's
                    // reconciler only updates DOM properties whose value actually
                    // changed between renders — for a small drag mostly in one
                    // direction, the unchanged axis (e.g. center.y after a horizontal
                    // drag) compares equal to the pre-drag value and React doesn't
                    // re-set that side's inline style. If we'd cleared first, the
                    // element would render with that axis omitted entirely (invalid
                    // CSS, the DOM drops the property), making the piece visible in
                    // the inspector but not on screen. Letting React overwrite only
                    // the changed axis preserves the drag-time inline value on the
                    // other axis — which is correct because that axis didn't move.
                    // Mirrors the multi-select drop receiver at the bottom of this
                    // file (multiSelectMoveEnd handler).
                    setManipulative(function (prev) {
                        return prev.map(function (m) {
                            var newCenter = finalPositions_1.get(m.data.id);
                            if (!newCenter)
                                return m;
                            return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: newCenter }) });
                        });
                    });
                    setPosition(groupSnapped);
                    var reorderChanges = getReorderStateChanges();
                    saveState(__spreadArray(__spreadArray([], stateChanges, true), reorderChanges, true));
                }
                else {
                    // Single piece snap — exclude co-moved pieces from snap detection during multi-select
                    var multiSelectExcludeIds_1 = isInMultiSelectStop
                        ? new Set(multiSelectStartPositions.current.map(function (p) { return p.id; }).filter(function (pid) { return pid !== id; }))
                        : undefined;
                    var snapped_1 = snapNMPPosition(posRef.current.x, posRef.current.y, w, h, multiSelectExcludeIds_1);
                    var didSnap = snapped_1.x !== posRef.current.x || snapped_1.y !== posRef.current.y;
                    var finalPos_1 = didSnap ? snapped_1 : posRef.current;
                    // Determine snappedId: find grid-aligned same-type pieces at the final position.
                    // "Grid-aligned" means TL offsets are multiples of PIECE_GRID * scale.
                    // Includes both edge-adjacent and overlapping placements (e.g. tens inside hundreds).
                    // Always check regardless of didSnap — piece may already be at snap position on release.
                    var touching_1 = [];
                    var newSnappedId_1;
                    if (isFullClusterSelectedRef.current) {
                        // The whole cluster moved rigidly together (same delta applied to every
                        // member) — relative alignment cannot have changed, so skip the
                        // drift-sensitive grid-alignment recompute entirely and just keep the
                        // cluster's existing snappedId, mirroring the group-drop path.
                        newSnappedId_1 = data.snappedId;
                    }
                    else {
                        var _k = getSnapDims(data), sw_1 = _k.w, sh_1 = _k.h;
                        var myTLX_1 = finalPos_1.x - sw_1 / 2;
                        var myTLY_1 = finalPos_1.y - sh_1 / 2;
                        var gridStep_1 = PIECE_GRID * ((_a = data.scale) !== null && _a !== void 0 ? _a : 1);
                        var TOL_1 = 1;
                        touching_1 = manipulativeRef.current.filter(function (m) {
                            if (m.data.id === id || m.data.type !== data.type)
                                return false;
                            // Co-selected pieces moved rigidly by the same delta — their adjacency
                            // to this piece (if any) predates the drag and can't have been newly
                            // created by it, so they must not be treated as new snap candidates.
                            // Without this, an unselected/unsnapped piece dragged as the leader of
                            // a multi-select that happens to sit next to a snapped cluster would
                            // spuriously "adopt" that cluster's snappedId on every drop.
                            if (multiSelectExcludeIds_1 === null || multiSelectExcludeIds_1 === void 0 ? void 0 : multiSelectExcludeIds_1.has(m.data.id))
                                return false;
                            var od = m.data;
                            var _a = getSnapDims(od), ow = _a.w, oh = _a.h;
                            var oTLX = od.center.x - ow / 2;
                            var oTLY = od.center.y - oh / 2;
                            // Must overlap or be edge-adjacent
                            var hOverlap = myTLX_1 < oTLX + ow + TOL_1 && myTLX_1 + sw_1 > oTLX - TOL_1;
                            var vOverlap = myTLY_1 < oTLY + oh + TOL_1 && myTLY_1 + sh_1 > oTLY - TOL_1;
                            if (!hOverlap || !vOverlap)
                                return false;
                            // hOverlap/vOverlap alone allow a corner-only touch — two pieces
                            // offset by exactly one full cell on BOTH axes are simultaneously
                            // "flush" on x and "flush" on y, sharing only a single diagonal
                            // point with zero real area overlap. That must not count as
                            // touching; require genuine (non-boundary) overlap on at least
                            // one axis, mirroring solveNmpPieceSnap's diagonal exclusion.
                            var hRealOverlap = myTLX_1 < oTLX + ow - TOL_1 && myTLX_1 + sw_1 > oTLX + TOL_1;
                            var vRealOverlap = myTLY_1 < oTLY + oh - TOL_1 && myTLY_1 + sh_1 > oTLY + TOL_1;
                            if (!hRealOverlap && !vRealOverlap)
                                return false;
                            // TL offset must be a multiple of gridStep in both axes
                            var dx = myTLX_1 - oTLX;
                            var dy = myTLY_1 - oTLY;
                            var xAligned = Math.abs(Math.round(dx / gridStep_1) * gridStep_1 - dx) < TOL_1;
                            var yAligned = Math.abs(Math.round(dy / gridStep_1) * gridStep_1 - dy) < TOL_1;
                            return xAligned && yAligned;
                        });
                        if (touching_1.length > 0) {
                            newSnappedId_1 =
                                (_d = (_c = (_b = touching_1.find(function (m) { return m.data.snappedId; })) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.snappedId) !== null && _d !== void 0 ? _d : uuidv4().replace(/-/g, '');
                        }
                    }
                    if (didSnap) {
                        setPosition(snapped_1);
                    }
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.id === id)
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: finalPos_1, snappedId: newSnappedId_1 }) });
                            if (newSnappedId_1 &&
                                touching_1.some(function (t) { return t.data.id === item.data.id; }) &&
                                !item.data.snappedId)
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { snappedId: newSnappedId_1 }) });
                            return item;
                        });
                    });
                    var reorderChanges = getReorderStateChanges();
                    var isMultiSelect = isInMultiSelectStop && multiSelectStartPositions.current.length > 1;
                    if (isMultiSelect) {
                        // Batch: apply same total delta to all selected NMP pieces
                        var selfStart = multiSelectStartPositions.current.find(function (p) { return p.id === id; });
                        if (selfStart) {
                            var totalDx_1 = snapped_1.x - selfStart.center.x;
                            var totalDy_1 = snapped_1.y - selfStart.center.y;
                            // Update non-dragged pieces in state (snap-corrected); also assign snappedId to co-moved pieces
                            setManipulative(function (prev) {
                                return prev.map(function (m) {
                                    var startPos = multiSelectStartPositions.current.find(function (p) { return p.id === m.data.id; });
                                    if (!startPos || m.data.id === id)
                                        return m;
                                    // Clamp so a large dragger delta can't push co-moved pieces
                                    // off-canvas. Each piece uses its own dims since a
                                    // multi-select can mix denominations.
                                    var memberData = m.data;
                                    var memberDims = getSnapDims(memberData);
                                    var clamped = clampCenter(startPos.center.x + totalDx_1, startPos.center.y + totalDy_1, memberDims.w, memberDims.h);
                                    return __assign(__assign({}, m), { data: __assign(__assign(__assign({}, m.data), { center: clamped }), (newSnappedId_1 &&
                                            (memberData.snappedId === newSnappedId_1 ||
                                                touching_1.some(function (t) { return t.data.id === m.data.id; })) && {
                                            snappedId: newSnappedId_1,
                                        })) });
                                });
                            });
                            var batchSave = multiSelectStartPositions.current.map(function (p) {
                                // Mirror the clamped center we actually committed above so
                                // undo can round-trip exactly.
                                var memberItem = manipulativeRef.current.find(function (m) { return m.data.id === p.id; });
                                var memberData = memberItem === null || memberItem === void 0 ? void 0 : memberItem.data;
                                var memberDims = memberData ? getSnapDims(memberData) : { w: 0, h: 0 };
                                var newCenter = p.id === id
                                    ? snapped_1
                                    : memberDims.w
                                        ? clampCenter(p.center.x + totalDx_1, p.center.y + totalDy_1, memberDims.w, memberDims.h)
                                        : { x: p.center.x + totalDx_1, y: p.center.y + totalDy_1 };
                                return {
                                    type: p.id,
                                    id: p.id.split('_')[1],
                                    propertyName: 'center',
                                    old: JSON.stringify(p.center),
                                    new: JSON.stringify(newCenter),
                                };
                            });
                            var multiSelectEntries = __spreadArray(__spreadArray([], batchSave, true), reorderChanges, true);
                            if (!registerMultiSelectSaveEntries(id, multiSelectEntries)) {
                                saveState(multiSelectEntries);
                            }
                        }
                        customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: id });
                    }
                    else {
                        var oldPos = dragStartItem.current;
                        if (oldPos) {
                            var snappedIdChanges = data.snappedId !== newSnappedId_1
                                ? [
                                    {
                                        type: id,
                                        id: id.split('_')[1],
                                        propertyName: 'snappedId',
                                        old: JSON.stringify((_e = data.snappedId) !== null && _e !== void 0 ? _e : null),
                                        new: JSON.stringify(newSnappedId_1 !== null && newSnappedId_1 !== void 0 ? newSnappedId_1 : null),
                                    },
                                ]
                                : [];
                            saveState(__spreadArray(__spreadArray([
                                {
                                    type: id,
                                    id: id.split('_')[1],
                                    propertyName: 'center',
                                    old: JSON.stringify(oldPos),
                                    new: JSON.stringify(finalPos_1),
                                }
                            ], snappedIdChanges, true), reorderChanges, true));
                        }
                        else if (reorderChanges.length > 0) {
                            saveState(reorderChanges);
                        }
                    }
                }
            }
            // No drag — a plain click never reorders the array (matches FractionTile).
            // Select here too (not just via ButtonComponent's native `clickHandler`)
            // — when the bring-to-front reorder above actually moves this piece's
            // DOM node (array position change), the browser can fail to dispatch
            // the subsequent native `click` event at all, since the element that
            // received mousedown no longer occupies the same DOM position by the
            // time mouseup finishes. Setting selection here makes it independent
            // of that native click firing. Guarded by selectionAppliedRef (reset via
            // setTimeout) so if the native click DOES still fire, it's skipped instead
            // of re-toggling a multi-select modifier click back off.
            var targetId = (_f = data.groupId) !== null && _f !== void 0 ? _f : id;
            selectionAppliedRef.current = true;
            setTimeout(function () {
                selectionAppliedRef.current = false;
            }, 0);
            setSelectedTool(selectTool(e, targetId, selectedTool, false));
            groupDragSnapshotRef.current = {};
            groupBoxStartRef.current = null;
            setIsDragging(false);
            dragStated.current = false;
            isDraggingRef.current = false;
            dragStartRef.current = null;
            setDragStart(null);
        };
        if (dragStated.current && dragStart !== null && !panningEnable) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', stopDrag);
            window.addEventListener('touchmove', handleMove, { passive: false });
            window.addEventListener('touchend', stopDrag);
            window.addEventListener('touchcancel', stopDrag);
        }
        return function () {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', stopDrag);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', stopDrag);
            window.removeEventListener('touchcancel', stopDrag);
        };
    }, [dragStart, panningEnable, positionLock, zoomLevel, id]);
    // Multi-select receiver: follow when another selected NMP piece is being dragged.
    // Uses direct DOM style updates instead of setManipulative to avoid 499 React
    // re-renders per mousemove. Final positions are committed in one batch on mouseup.
    var multiDragStartRef = useRef({ x: 0, y: 0 });
    useEffect(function () {
        var isSelected = selectedTool.includes(id) || (!!data.groupId && selectedTool.includes(data.groupId));
        if (!isSelected || selectedTool.length <= 1)
            return;
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            // sourceId === data.groupId means the group overlay itself (NumberAndMoneyPieceSelectionGroup.tsx)
            // is leading — it already moves every member directly via setManipulative, so this
            // listener must stay out of the way or it double-applies the same delta on top.
            if (sourceId === id || (!!data.groupId && sourceId === data.groupId))
                return;
            multiDragStartRef.current = { x: centerRef.current.x, y: centerRef.current.y };
        };
        var onMove = function (_a) {
            var deltaX = _a.deltaX, deltaY = _a.deltaY, sourceId = _a.sourceId;
            if (sourceId === id || (!!data.groupId && sourceId === data.groupId) || positionLock)
                return;
            var el = wrapperRef.current;
            if (!el)
                return;
            el.style.left = "".concat(multiDragStartRef.current.x + deltaX, "px");
            el.style.top = "".concat(multiDragStartRef.current.y + deltaY, "px");
        };
        var onEnd = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === id || (!!data.groupId && sourceId === data.groupId))
                return;
            // Do NOT clear inline styles here — React will overwrite them when it commits
            // the new data.center from setManipulative. Clearing causes a 1-frame flash where
            // the piece jumps back to the pre-drag position before React commits.
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
        };
    }, [selectedTool, id, data.groupId, positionLock]);
    // Bottom panel events
    useEffect(function () {
        var onToolClick = function (toolData) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
            // Skip scale — handled by ManipulativeGroup (isGroup=true) or GameController (data.groupId set)
            if ((isGroup || !!data.groupId) &&
                (toolData.id === 'scaleMinus' || toolData.id === 'scalePlus'))
                return;
            // Resolve group ID to member piece IDs for operations
            var currentSelected = __spreadArray([], selectedToolRef.current, true);
            if (currentSelected.length === 1 && currentSelected[0].startsWith('manipulativeGroup_')) {
                currentSelected = manipulative
                    .filter(function (m) { return m.data.groupId === currentSelected[0]; })
                    .map(function (m) { return m.data.id; });
            }
            switch (toolData.id) {
                case 'edit':
                    setActiveManipulator('moneyPieces');
                    setManiplulatorComponentVisible(true);
                    setSelectedManipulativeId(id);
                    break;
                case 'scaleMinus': {
                    // Guard: among all selected NMP pieces, only the first one runs scale to prevent
                    // the handler firing N times (once per focused piece) causing ×N scale jumps.
                    var firstSelectedNmpId = currentSelected.find(function (sid) { return sid.startsWith('numberPiece_') || sid.startsWith('moneyPiece_'); });
                    if (firstSelectedNmpId !== id)
                        break;
                    var pieceDataType_1 = data.type;
                    var sameType = manipulative.filter(function (m) { return m.data.type === pieceDataType_1; });
                    // Build cluster position updates so snapped pieces stay touching after scale
                    var clusterPositionUpdates_1 = new Map();
                    var clusterMap_1 = new Map();
                    sameType.forEach(function (m) {
                        var sid = m.data.snappedId;
                        if (sid) {
                            if (!clusterMap_1.has(sid))
                                clusterMap_1.set(sid, []);
                            clusterMap_1.get(sid).push(m);
                        }
                    });
                    clusterMap_1.forEach(function (members) {
                        var _a;
                        if (members.length < 2)
                            return;
                        var oldScale = (_a = members[0].data.scale) !== null && _a !== void 0 ? _a : 1;
                        var newScale = Math.max(Math.round((oldScale - 0.1) * 10) / 10, 0.5);
                        var oldStep = PIECE_GRID * oldScale;
                        var newStep = PIECE_GRID * newScale;
                        var withTL = members.map(function (m) {
                            var d = m.data;
                            var _a = getSnapDims(d), w = _a.w, h = _a.h;
                            return {
                                id: m.data.id,
                                d: d,
                                tlX: d.center.x - w / 2,
                                tlY: d.center.y - h / 2,
                            };
                        });
                        withTL.sort(function (a, b) { return a.tlY - b.tlY || a.tlX - b.tlX; });
                        var anchor = withTL[0];
                        // Anchor center stays fixed; compute its new TL from its center at the new scale
                        var anchorBase = getBaseDims(anchor.d);
                        var anchorNewTLX = anchor.d.center.x - (anchorBase.w * newScale) / 2;
                        var anchorNewTLY = anchor.d.center.y - (anchorBase.h * newScale) / 2;
                        // Only reposition non-anchor pieces relative to the anchor's new TL
                        withTL.slice(1).forEach(function (_a) {
                            var mid = _a.id, d = _a.d, tlX = _a.tlX, tlY = _a.tlY;
                            var gx = Math.round((tlX - anchor.tlX) / oldStep);
                            var gy = Math.round((tlY - anchor.tlY) / oldStep);
                            var base = getBaseDims(d);
                            var newW = base.w * newScale;
                            var newH = base.h * newScale;
                            clusterPositionUpdates_1.set(mid, {
                                x: anchorNewTLX + gx * newStep + newW / 2,
                                y: anchorNewTLY + gy * newStep + newH / 2,
                            });
                        });
                    });
                    var scaleMinusChanges = sameType.flatMap(function (m) {
                        var _a;
                        var cur = (_a = m.data.scale) !== null && _a !== void 0 ? _a : 1;
                        var changes = [
                            {
                                type: m.data.id,
                                id: m.data.id.split('_')[1],
                                propertyName: 'scale',
                                old: JSON.stringify(cur),
                                new: JSON.stringify(Math.max(Math.round((cur - 0.1) * 10) / 10, 0.5)),
                            },
                        ];
                        var newCenter = clusterPositionUpdates_1.get(m.data.id);
                        if (newCenter) {
                            changes.push({
                                type: m.data.id,
                                id: m.data.id.split('_')[1],
                                propertyName: 'center',
                                old: JSON.stringify(m.data.center),
                                new: JSON.stringify(newCenter),
                            });
                        }
                        return changes;
                    });
                    saveState(scaleMinusChanges);
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (item.data.type !== pieceDataType_1)
                                return item;
                            var cur = (_a = item.data.scale) !== null && _a !== void 0 ? _a : 1;
                            var newCenter = clusterPositionUpdates_1.get(item.data.id);
                            return __assign(__assign({}, item), { data: __assign(__assign(__assign({}, item.data), { scale: Math.max(Math.round((cur - 0.1) * 10) / 10, 0.5) }), (newCenter && { center: newCenter })) });
                        });
                    });
                    // Announce new scale value for SR users — assertive so rapid +/- clicks
                    // each produce immediate feedback. Use this piece's resulting scale as
                    // the representative value (all same-type pieces scale together).
                    {
                        var cur = (_a = data.scale) !== null && _a !== void 0 ? _a : 1;
                        var next = Math.max(Math.round((cur - 0.1) * 10) / 10, 0.5);
                        if (next !== cur) {
                            announceAssertive(t('text.numberPieceScaled', {
                                type: t(pieceDataType_1 === 'numberPiece' ? 'text.numberPiece' : 'text.moneyPiece'),
                                direction: t('text.down'),
                                value: Math.round(next * 100),
                            }));
                        }
                    }
                    break;
                }
                case 'scalePlus': {
                    // Guard: same as scaleMinus — only first selected NMP piece runs this.
                    var firstSelectedNmpId = currentSelected.find(function (sid) { return sid.startsWith('numberPiece_') || sid.startsWith('moneyPiece_'); });
                    if (firstSelectedNmpId !== id)
                        break;
                    var pieceDataType_2 = data.type;
                    var sameType = manipulative.filter(function (m) { return m.data.type === pieceDataType_2; });
                    // Build cluster position updates so snapped pieces stay touching after scale
                    var clusterPositionUpdates_2 = new Map();
                    var clusterMap_2 = new Map();
                    sameType.forEach(function (m) {
                        var sid = m.data.snappedId;
                        if (sid) {
                            if (!clusterMap_2.has(sid))
                                clusterMap_2.set(sid, []);
                            clusterMap_2.get(sid).push(m);
                        }
                    });
                    clusterMap_2.forEach(function (members) {
                        var _a;
                        if (members.length < 2)
                            return;
                        var oldScale = (_a = members[0].data.scale) !== null && _a !== void 0 ? _a : 1;
                        var newScale = Math.min(Math.round((oldScale + 0.1) * 10) / 10, 5);
                        var oldStep = PIECE_GRID * oldScale;
                        var newStep = PIECE_GRID * newScale;
                        var withTL = members.map(function (m) {
                            var d = m.data;
                            var _a = getSnapDims(d), w = _a.w, h = _a.h;
                            return {
                                id: m.data.id,
                                d: d,
                                tlX: d.center.x - w / 2,
                                tlY: d.center.y - h / 2,
                            };
                        });
                        withTL.sort(function (a, b) { return a.tlY - b.tlY || a.tlX - b.tlX; });
                        var anchor = withTL[0];
                        // Anchor center stays fixed; compute its new TL from its center at the new scale
                        var anchorBase = getBaseDims(anchor.d);
                        var anchorNewTLX = anchor.d.center.x - (anchorBase.w * newScale) / 2;
                        var anchorNewTLY = anchor.d.center.y - (anchorBase.h * newScale) / 2;
                        // Only reposition non-anchor pieces relative to the anchor's new TL
                        withTL.slice(1).forEach(function (_a) {
                            var mid = _a.id, d = _a.d, tlX = _a.tlX, tlY = _a.tlY;
                            var gx = Math.round((tlX - anchor.tlX) / oldStep);
                            var gy = Math.round((tlY - anchor.tlY) / oldStep);
                            var base = getBaseDims(d);
                            var newW = base.w * newScale;
                            var newH = base.h * newScale;
                            clusterPositionUpdates_2.set(mid, {
                                x: anchorNewTLX + gx * newStep + newW / 2,
                                y: anchorNewTLY + gy * newStep + newH / 2,
                            });
                        });
                    });
                    var scalePlusChanges = sameType.flatMap(function (m) {
                        var _a;
                        var cur = (_a = m.data.scale) !== null && _a !== void 0 ? _a : 1;
                        var changes = [
                            {
                                type: m.data.id,
                                id: m.data.id.split('_')[1],
                                propertyName: 'scale',
                                old: JSON.stringify(cur),
                                new: JSON.stringify(Math.min(Math.round((cur + 0.1) * 10) / 10, 5)),
                            },
                        ];
                        var newCenter = clusterPositionUpdates_2.get(m.data.id);
                        if (newCenter) {
                            changes.push({
                                type: m.data.id,
                                id: m.data.id.split('_')[1],
                                propertyName: 'center',
                                old: JSON.stringify(m.data.center),
                                new: JSON.stringify(newCenter),
                            });
                        }
                        return changes;
                    });
                    saveState(scalePlusChanges);
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (item.data.type !== pieceDataType_2)
                                return item;
                            var cur = (_a = item.data.scale) !== null && _a !== void 0 ? _a : 1;
                            var newCenter = clusterPositionUpdates_2.get(item.data.id);
                            return __assign(__assign({}, item), { data: __assign(__assign(__assign({}, item.data), { scale: Math.min(Math.round((cur + 0.1) * 10) / 10, 5) }), (newCenter && { center: newCenter })) });
                        });
                    });
                    {
                        var cur = (_b = data.scale) !== null && _b !== void 0 ? _b : 1;
                        var next = Math.min(Math.round((cur + 0.1) * 10) / 10, 5);
                        if (next !== cur) {
                            announceAssertive(t('text.numberPieceScaled', {
                                type: t(pieceDataType_2 === 'numberPiece' ? 'text.numberPiece' : 'text.moneyPiece'),
                                direction: t('text.up'),
                                value: Math.round(next * 100),
                            }));
                        }
                    }
                    break;
                }
                case 'nmp_break_colored':
                case 'nmp_break': {
                    var currentSelectedTool = currentSelected;
                    var breakNextDenom_1 = {
                        hundreds: 'tens',
                        tens: 'ones',
                    };
                    var breakDimMap_1 = PIECE_DIMENSIONS;
                    // Collect all selected breakable pieces
                    var breakableItems_1 = currentSelectedTool
                        .map(function (sid) { return manipulative.find(function (m) { return m.data.id === sid; }); })
                        .filter(function (m) {
                        if (!m || m.data.type !== 'numberPiece')
                            return false;
                        return m.data.denomination !== 'ones';
                    });
                    if (breakableItems_1.length === 0)
                        break;
                    // Guard: only the first BREAKABLE component handles break
                    // (must be a piece that gets animating:true so onAnimationEnd fires)
                    if (id !== breakableItems_1[0].data.id)
                        break;
                    var allNewPieces_1 = [];
                    var stateChanges_1 = [];
                    var deleteIds_1 = [];
                    breakableItems_1.forEach(function (item) {
                        var np = item.data;
                        var smallerDenom = breakNextDenom_1[np.denomination];
                        var newDims = breakDimMap_1[smallerDenom];
                        var oldDims = breakDimMap_1[np.denomination];
                        var sc = np.scale || 1;
                        stateChanges_1.push({
                            type: np.id,
                            id: np.id,
                            propertyName: 'delete',
                            old: JSON.stringify(item),
                        });
                        deleteIds_1.push(np.id);
                        var rot = ((np.rotation || 0) * Math.PI) / 180;
                        var cosR = Math.cos(rot);
                        var sinR = Math.sin(rot);
                        // Standalone horizontal tens are stored with swapped dimensions
                        // (width > height, rotation = 0) instead of CSS-rotated, so we
                        // detect orientation from the actual dims rather than the
                        // canonical breakDimMap entry.
                        var actualDims = np.dimensions || np.defaultDimensions || oldDims;
                        var isHorizontalParent = actualDims.width > actualDims.height;
                        // Small gap between broken children so each one reads as a
                        // separate piece. Note: this makes the broken group ~45 px wider
                        // than the parent's footprint (10×55 + 9×5 = 595 vs 550), so
                        // densely-packed parents can still end up with ~5 px overlap.
                        var breakGap = 5;
                        for (var i = 0; i < 10; i++) {
                            var localOffsetX = void 0, localOffsetY = void 0;
                            if (np.denomination === 'hundreds') {
                                // Tens arranged horizontally in local frame
                                localOffsetX =
                                    (-oldDims.width / 2 + newDims.width / 2 + i * (newDims.width + breakGap)) * sc;
                                localOffsetY = 0;
                            }
                            else if (isHorizontalParent) {
                                // Horizontal ten → ones arranged in a row matching the parent
                                localOffsetX =
                                    (-actualDims.width / 2 + newDims.width / 2 + i * (newDims.width + breakGap)) * sc;
                                localOffsetY = 0;
                            }
                            else {
                                // Vertical ten → ones arranged in a column
                                localOffsetX = 0;
                                localOffsetY =
                                    (-actualDims.height / 2 + newDims.height / 2 + i * (newDims.height + breakGap)) *
                                        sc;
                            }
                            // Rotate offset by parent's rotation angle, then clamp to canvas
                            var cx0 = np.center.x + localOffsetX * cosR - localOffsetY * sinR;
                            var cy0 = np.center.y + localOffsetX * sinR + localOffsetY * cosR;
                            var _a = clampCenter(cx0, cy0, newDims.width * sc, newDims.height * sc), cx = _a.x, cy = _a.y;
                            var newId = "numberPiece_".concat(uuidv4().replace(/-/g, ''));
                            allNewPieces_1.push({
                                type: 'numberPiece',
                                widgetId: "numberPiece:".concat(uuidv4().replace(/-/g, '')),
                                data: __assign({ id: newId, type: 'numberPiece', pieceType: np.pieceType, denomination: smallerDenom, animation: np.animation, color: np.color, center: { x: cx, y: cy }, dimensions: __assign({}, newDims), defaultDimensions: __assign({}, newDims), scale: sc, rotation: 0, lock: { positioning: false, editing: false } }, (np.groupId
                                    ? {
                                        groupId: np.groupId,
                                        groupScale: np.groupScale,
                                        beforeGroupScale: np.beforeGroupScale,
                                        groupCenter: np.groupCenter,
                                    }
                                    : {})),
                            });
                        }
                    });
                    // Re-index groupPosition for all group members after break
                    if (allNewPieces_1.length > 0 && allNewPieces_1[0].data.groupId) {
                        var grpId_1 = allNewPieces_1[0].data.groupId;
                        var existingMembers = manipulative
                            .filter(function (m) { return m.data.groupId === grpId_1 && !breakableItems_1.some(function (b) { return b.data.id === m.data.id; }); })
                            .sort(function (a, b) { var _a, _b; return ((_a = a.data.groupPosition) !== null && _a !== void 0 ? _a : 0) - ((_b = b.data.groupPosition) !== null && _b !== void 0 ? _b : 0); });
                        var pos_1 = 0;
                        existingMembers.forEach(function (m) {
                            m.data.groupPosition = pos_1++;
                        });
                        allNewPieces_1.forEach(function (p) {
                            p.data.groupPosition = pos_1++;
                        });
                    }
                    // stateChanges uses clean data (no animating flag)
                    stateChanges_1.push({
                        type: allNewPieces_1[0].data.id,
                        id: allNewPieces_1[0].data.id.split('_')[1],
                        propertyName: 'batchCreate',
                        old: JSON.stringify(allNewPieces_1),
                    });
                    var allAnimationOn = breakableItems_1.every(function (item) { return item.data.animation !== false; });
                    if (allAnimationOn) {
                        // === ANIMATED BREAK ===
                        var newPieceIds_1 = allNewPieces_1.map(function (p) { return p.data.id; });
                        var breakGroupId_1 = (_d = (_c = allNewPieces_1[0]) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.groupId;
                        // t=0: Mark old pieces animatingOut → inline fade-out starts; ticker → buttons disable
                        nmpAnimationTicker.setAnimating(true);
                        setManipulative(function (prev) {
                            return prev.map(function (m) {
                                return deleteIds_1.includes(m.data.id)
                                    ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { animatingOut: true }) }) : m;
                            });
                        });
                        // t=1s: Add new pieces — they read ticker at mount (isAnimating=true) → fade in.
                        // Spliced in just BEFORE the still-present (animatingOut) old pieces —
                        // rather than appended to the array end — so the old pieces keep
                        // rendering on top during the fade, purely from array position
                        // (no zIndex needed).
                        setTimeout(function () {
                            var _loop_5 = function (start) {
                                var chunk = allNewPieces_1.slice(start, start + 50);
                                requestAnimationFrame(function () {
                                    setManipulative(function (prev) {
                                        var firstOldIdx = prev.findIndex(function (m) {
                                            return deleteIds_1.includes(m.data.id);
                                        });
                                        var insertAt = firstOldIdx === -1 ? prev.length : firstOldIdx;
                                        var next = __spreadArray([], prev, true);
                                        next.splice.apply(next, __spreadArray([insertAt, 0], chunk, false));
                                        return next;
                                    });
                                });
                            };
                            for (var start = 0; start < allNewPieces_1.length; start += 50) {
                                _loop_5(start);
                            }
                        }, 1000);
                        // t=3.1s: Remove old pieces, save state, update selection; ticker → buttons enable
                        setTimeout(function () {
                            var deleteIdSet = new Set(deleteIds_1);
                            setManipulative(function (prev) {
                                return bringManipulativesToFront(prev
                                    .filter(function (m) { return !deleteIdSet.has(m.data.id); })
                                    .map(function (m) {
                                    return m.data.animatingOut
                                        ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { animatingOut: undefined }) }) : m;
                                }), 
                                // Old pieces are gone now, so promote the new pieces to the
                                // true front of the array — the earlier splice only kept
                                // them behind their own old pieces during the fade.
                                newPieceIds_1);
                            });
                            nmpAnimationTicker.setAnimating(false);
                            saveState(stateChanges_1);
                            // Don't auto-select the new pieces — keeping them selected leaks
                            // a "selected" SR announcement onto every resulting piece, which
                            // is noisy for break results (e.g. 100 → 100×ones). Preserve
                            // the group selection if the source was grouped (group is a
                            // logical container, not "the items").
                            setSelectedTool(breakGroupId_1 ? [breakGroupId_1] : []);
                        }, 3100);
                        playAudio('breakNMP');
                    }
                    else {
                        // === INSTANT BREAK (no animation) ===
                        saveState(stateChanges_1);
                        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return !deleteIds_1.includes(m.data.id); }), true), allNewPieces_1, true); });
                        // If pieces are in a group, keep the group selected; otherwise
                        // clear selection (see comment in animated branch above).
                        var breakGroupId = (_f = (_e = allNewPieces_1[0]) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.groupId;
                        setSelectedTool(breakGroupId ? [breakGroupId] : []);
                        playAudio('breakNMP');
                    }
                    // Announce the break — uses the first broken item to describe
                    // from/to denominations; count = 10 per source piece × source count.
                    {
                        var firstItem = breakableItems_1[0].data;
                        var fromDenom = firstItem.denomination;
                        var toDenom = breakNextDenom_1[fromDenom];
                        var totalNew = breakableItems_1.length * 10;
                        announce(t('text.numberPieceBroken', {
                            from: t("text.".concat(fromDenom)),
                            to: t("text.".concat(toDenom)),
                            count: totalNew,
                        }));
                    }
                    break;
                }
                case 'nmp_link_colored':
                case 'nmp_link': {
                    var linkNextDenom_1 = {
                        ones: 'tens',
                        tens: 'hundreds',
                    };
                    var linkDimMap = PIECE_DIMENSIONS;
                    // Gather all selected numberPieces grouped by denomination
                    var denomOrder = ['ones', 'tens'];
                    var piecesByDenom_1 = {};
                    currentSelected.forEach(function (sid) {
                        var m = manipulative.find(function (item) { return item.data.id === sid; });
                        if (!m || m.data.type !== 'numberPiece')
                            return;
                        var denom = m.data.denomination;
                        if (!linkNextDenom_1[denom])
                            return; // hundreds can't link
                        if (!piecesByDenom_1[denom])
                            piecesByDenom_1[denom] = [];
                        piecesByDenom_1[denom].push(m);
                    });
                    // Find first denomination that has 10+ pieces (linkable)
                    var firstLinkableDenom = denomOrder.find(function (d) { return piecesByDenom_1[d] && piecesByDenom_1[d].length >= 10; });
                    if (!firstLinkableDenom)
                        break;
                    // Guard: only the first LINKABLE component handles link
                    // (must be a piece that gets animating:true so onAnimationEnd fires)
                    if (id !== piecesByDenom_1[firstLinkableDenom][0].data.id)
                        break;
                    // Determine if mixed denominations (cascade) or single denomination (one level)
                    var activeDenoms = denomOrder.filter(function (d) { return piecesByDenom_1[d] && piecesByDenom_1[d].length > 0; });
                    var isMixed = activeDenoms.length > 1;
                    // Track counts per denomination for cascade logic
                    // For mixed: start with actual pieces, then carry forward newly created pieces
                    var denomCounts = {};
                    for (var _i = 0, denomOrder_1 = denomOrder; _i < denomOrder_1.length; _i++) {
                        var denom = denomOrder_1[_i];
                        denomCounts[denom] = piecesByDenom_1[denom] ? piecesByDenom_1[denom].length : 0;
                    }
                    var linkStateChanges_1 = [];
                    var linkDeleteIds_1 = [];
                    var allNewPiecesLink_1 = [];
                    // Process denominations from smallest to largest
                    // For single denomination: only process that one level
                    // For mixed: cascade — newly created pieces add to next denomination's count
                    var processedAny = false;
                    var _loop_3 = function (denom) {
                        var count = denomCounts[denom];
                        if (count < 10)
                            return "continue";
                        var consumeCount = Math.floor(count / 10) * 10;
                        var newCount = consumeCount / 10;
                        var biggerDenom = linkNextDenom_1[denom];
                        var newDims = linkDimMap[biggerDenom];
                        // Consume actual pieces — hybrid: group by color, leftover by position
                        var actualPieces = piecesByDenom_1[denom] || [];
                        // Helper: create linked pieces from a consumed array (per-group-of-10)
                        var createLinkedPieces = function (src, fb) {
                            var srcCount = Math.floor(src.length / 10);
                            for (var i = 0; i < srcCount; i++) {
                                var gNp = i * 10 < src.length ? src[i * 10].data : fb;
                                var gStart = i * 10;
                                var gEnd = Math.min(gStart + 10, src.length);
                                var gSlice = src.slice(gStart, gEnd);
                                var cx = void 0, cy = void 0;
                                if (gSlice.length > 0) {
                                    cx =
                                        gSlice.reduce(function (s, m) { return s + m.data.center.x; }, 0) /
                                            gSlice.length;
                                    cy =
                                        gSlice.reduce(function (s, m) { return s + m.data.center.y; }, 0) /
                                            gSlice.length;
                                }
                                else {
                                    var last = allNewPiecesLink_1[allNewPiecesLink_1.length - 1].data;
                                    cx = last.center.x;
                                    cy = last.center.y;
                                }
                                // Inherit orientation from source layout: when 10 ones are
                                // linked into a tens, a horizontal row of ones should produce
                                // a horizontal tens, not the default vertical bar. Detect via
                                // the bounding-box aspect of the consumed group's centers; we
                                // only swap for tens since ones/hundreds are square.
                                var pieceDims = newDims;
                                if (biggerDenom === 'tens' && gSlice.length >= 2) {
                                    var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
                                    for (var _i = 0, gSlice_1 = gSlice; _i < gSlice_1.length; _i++) {
                                        var m = gSlice_1[_i];
                                        var c = m.data.center;
                                        if (c.x < minX)
                                            minX = c.x;
                                        if (c.x > maxX)
                                            maxX = c.x;
                                        if (c.y < minY)
                                            minY = c.y;
                                        if (c.y > maxY)
                                            maxY = c.y;
                                    }
                                    if (maxX - minX > maxY - minY) {
                                        pieceDims = { width: newDims.height, height: newDims.width };
                                    }
                                }
                                var clamped = clampCenter(cx, cy, pieceDims.width * (gNp.scale || 1), pieceDims.height * (gNp.scale || 1));
                                cx = clamped.x;
                                cy = clamped.y;
                                var newId = "numberPiece_".concat(uuidv4().replace(/-/g, ''));
                                allNewPiecesLink_1.push({
                                    type: 'numberPiece',
                                    widgetId: "numberPiece:".concat(uuidv4().replace(/-/g, '')),
                                    data: __assign({ id: newId, type: 'numberPiece', pieceType: gNp.pieceType, denomination: biggerDenom, animation: gNp.animation, color: gNp.color, center: { x: cx, y: cy }, dimensions: __assign({}, pieceDims), defaultDimensions: __assign({}, pieceDims), scale: gNp.scale || 1, rotation: 0, lock: { positioning: false, editing: false } }, (gNp.groupId
                                        ? {
                                            groupId: gNp.groupId,
                                            groupScale: gNp.groupScale,
                                            beforeGroupScale: gNp.beforeGroupScale,
                                            groupCenter: gNp.groupCenter,
                                        }
                                        : {})),
                                });
                            }
                        };
                        // Fallback for cascade level with no actual pieces
                        var fallbackNp = actualPieces.length > 0
                            ? actualPieces[0].data
                            : allNewPiecesLink_1.length > 0
                                ? allNewPiecesLink_1[allNewPiecesLink_1.length - 1].data
                                : null;
                        if (!fallbackNp && consumeCount > actualPieces.length) {
                            // Pure cascade — no actual pieces, use fallback
                        }
                        else if (!fallbackNp) {
                            return "continue";
                        }
                        // Step 1: Sub-group actual pieces by color
                        var colorKeyFn = function (m) {
                            var np = m.data;
                            return np.color || "".concat(np.pieceType, "_").concat(np.denomination);
                        };
                        var byColor = {};
                        actualPieces.forEach(function (m) {
                            var key = colorKeyFn(m);
                            if (!byColor[key])
                                byColor[key] = [];
                            byColor[key].push(m);
                        });
                        // Step 2: Pure color groups (>=10) link within color
                        var leftover = [];
                        var allConsumed = [];
                        for (var _9 = 0, _10 = Object.values(byColor); _9 < _10.length; _9++) {
                            var group = _10[_9];
                            if (group.length >= 10) {
                                var gc_1 = Math.floor(group.length / 10) * 10;
                                var consumed = group.slice(0, gc_1);
                                allConsumed.push.apply(allConsumed, consumed);
                                createLinkedPieces(consumed, fallbackNp);
                                leftover.push.apply(leftover, group.slice(gc_1));
                            }
                            else {
                                leftover.push.apply(leftover, group);
                            }
                        }
                        // Step 3: Leftover (mixed colors) — sort by position, then link
                        if (leftover.length >= 10) {
                            leftover.sort(function (a, b) {
                                var aP = a.data.center;
                                var bP = b.data.center;
                                return aP.x - bP.x || aP.y - bP.y;
                            });
                            var lc = Math.floor(leftover.length / 10) * 10;
                            var leftConsumed = leftover.slice(0, lc);
                            allConsumed.push.apply(allConsumed, leftConsumed);
                            createLinkedPieces(leftConsumed, fallbackNp);
                        }
                        // Handle cascade-only virtual pieces (no actual pieces at this level)
                        if (actualPieces.length === 0 && consumeCount > 0) {
                            // All virtual from previous cascade level — use fallback
                            var cascadeCount = Math.floor(consumeCount / 10);
                            for (var i = 0; i < cascadeCount; i++) {
                                var fb = fallbackNp || allNewPiecesLink_1[allNewPiecesLink_1.length - 1].data;
                                var newId = "numberPiece_".concat(uuidv4().replace(/-/g, ''));
                                allNewPiecesLink_1.push({
                                    type: 'numberPiece',
                                    widgetId: "numberPiece:".concat(uuidv4().replace(/-/g, '')),
                                    data: __assign({ id: newId, type: 'numberPiece', pieceType: fb.pieceType, denomination: biggerDenom, animation: fb.animation, color: fb.color, center: { x: fb.center.x, y: fb.center.y }, dimensions: __assign({}, newDims), defaultDimensions: __assign({}, newDims), scale: fb.scale || 1, rotation: 0, lock: { positioning: false, editing: false } }, (fb.groupId
                                        ? {
                                            groupId: fb.groupId,
                                            groupScale: fb.groupScale,
                                            beforeGroupScale: fb.beforeGroupScale,
                                            groupCenter: fb.groupCenter,
                                        }
                                        : {})),
                                });
                            }
                        }
                        // Delete all consumed actual pieces
                        allConsumed.forEach(function (item) {
                            linkStateChanges_1.push({
                                type: item.data.id,
                                id: item.data.id,
                                propertyName: 'delete',
                                old: JSON.stringify(item),
                            });
                            linkDeleteIds_1.push(item.data.id);
                        });
                        if (allConsumed.length > 0 || (actualPieces.length === 0 && consumeCount > 0)) {
                            processedAny = true;
                        }
                        // For cascade: add newly created pieces to next denomination's count
                        if (isMixed && linkNextDenom_1[biggerDenom]) {
                            denomCounts[biggerDenom] = (denomCounts[biggerDenom] || 0) + newCount;
                        }
                        // For single denomination: stop after one level
                        if (!isMixed)
                            return "break";
                    };
                    for (var _s = 0, denomOrder_2 = denomOrder; _s < denomOrder_2.length; _s++) {
                        var denom = denomOrder_2[_s];
                        var state_1 = _loop_3(denom);
                        if (state_1 === "break")
                            break;
                    }
                    // Re-index groupPosition for all group members after link
                    if (allNewPiecesLink_1.length > 0 && allNewPiecesLink_1[0].data.groupId) {
                        var grpId_2 = allNewPiecesLink_1[0].data.groupId;
                        var existingMembers = manipulative
                            .filter(function (m) { return m.data.groupId === grpId_2 && !linkDeleteIds_1.includes(m.data.id); })
                            .sort(function (a, b) { var _a, _b; return ((_a = a.data.groupPosition) !== null && _a !== void 0 ? _a : 0) - ((_b = b.data.groupPosition) !== null && _b !== void 0 ? _b : 0); });
                        var pos_2 = 0;
                        existingMembers.forEach(function (m) {
                            m.data.groupPosition = pos_2++;
                        });
                        allNewPiecesLink_1.forEach(function (p) {
                            p.data.groupPosition = pos_2++;
                        });
                    }
                    if (!processedAny || allNewPiecesLink_1.length === 0)
                        break;
                    linkStateChanges_1.push({
                        type: allNewPiecesLink_1[0].data.id,
                        id: allNewPiecesLink_1[0].data.id.split('_')[1],
                        propertyName: 'batchCreate',
                        old: JSON.stringify(allNewPiecesLink_1),
                    });
                    var remainingIds = currentSelected.filter(function (sid) { return !linkDeleteIds_1.includes(sid); });
                    var linkNewPieceIds_1 = allNewPiecesLink_1.map(function (p) { return p.data.id; });
                    // Check if all consumed pieces have animation enabled
                    var allLinkAnimationOn = linkDeleteIds_1.every(function (sid) {
                        var m = manipulative.find(function (item) { return item.data.id === sid; });
                        return m && m.data.animation !== false;
                    });
                    if (allLinkAnimationOn && linkDeleteIds_1.length > 0) {
                        // === ANIMATED LINK ===
                        var linkGroupId_1 = (_h = (_g = allNewPiecesLink_1[0]) === null || _g === void 0 ? void 0 : _g.data) === null || _h === void 0 ? void 0 : _h.groupId;
                        // t=0: Mark old pieces animatingOut → inline fade-out starts; ticker → buttons disable
                        nmpAnimationTicker.setAnimating(true);
                        setManipulative(function (prev) {
                            return prev.map(function (m) {
                                return linkDeleteIds_1.includes(m.data.id)
                                    ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { animatingOut: true }) }) : m;
                            });
                        });
                        // t=1s: Add new pieces — they read ticker at mount (isAnimating=true) → fade in.
                        // Spliced in just BEFORE the still-present (animatingOut) old pieces
                        // so they keep rendering on top during the fade, via array
                        // position alone (no zIndex needed).
                        setTimeout(function () {
                            var _loop_6 = function (start) {
                                var chunk = allNewPiecesLink_1.slice(start, start + 50);
                                requestAnimationFrame(function () {
                                    setManipulative(function (prev) {
                                        var firstOldIdx = prev.findIndex(function (m) {
                                            return linkDeleteIds_1.includes(m.data.id);
                                        });
                                        var insertAt = firstOldIdx === -1 ? prev.length : firstOldIdx;
                                        var next = __spreadArray([], prev, true);
                                        next.splice.apply(next, __spreadArray([insertAt, 0], chunk, false));
                                        return next;
                                    });
                                });
                            };
                            for (var start = 0; start < allNewPiecesLink_1.length; start += 50) {
                                _loop_6(start);
                            }
                        }, 1000);
                        // t=3.1s: Remove old pieces, save state, update selection; ticker → buttons enable
                        setTimeout(function () {
                            var deleteIdSet = new Set(linkDeleteIds_1);
                            setManipulative(function (prev) {
                                return bringManipulativesToFront(prev
                                    .filter(function (m) { return !deleteIdSet.has(m.data.id); })
                                    .map(function (m) {
                                    return m.data.animatingOut
                                        ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { animatingOut: undefined }) }) : m;
                                }), linkNewPieceIds_1);
                            });
                            nmpAnimationTicker.setAnimating(false);
                            saveState(linkStateChanges_1);
                            // Don't auto-select the new linked pieces — see break comment.
                            setSelectedTool(linkGroupId_1 ? [linkGroupId_1] : []);
                        }, 3100);
                        playAudio('linkNMP');
                    }
                    else {
                        // === INSTANT LINK (no animation) ===
                        saveState(linkStateChanges_1);
                        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return !linkDeleteIds_1.includes(m.data.id); }), true), allNewPiecesLink_1, true); });
                        // If pieces are in a group, keep the group selected; otherwise
                        // clear selection (see break comment).
                        var linkGroupId = (_k = (_j = allNewPiecesLink_1[0]) === null || _j === void 0 ? void 0 : _j.data) === null || _k === void 0 ? void 0 : _k.groupId;
                        setSelectedTool(linkGroupId ? [linkGroupId] : []);
                        playAudio('linkNMP');
                    }
                    // Announce the link result. Each set of 10 small pieces becomes 1
                    // bigger piece; the new pieces' denomination tells us the "to" side.
                    if (allNewPiecesLink_1.length > 0) {
                        var firstNew = allNewPiecesLink_1[0].data;
                        var toDenom = firstNew.denomination;
                        // Reverse-map to find the source denom (10 of these became 1 of toDenom)
                        var reverseDenomMap = {
                            tens: 'ones',
                            hundreds: 'tens',
                            ones: null,
                        };
                        var fromDenom = reverseDenomMap[toDenom];
                        if (fromDenom) {
                            announce(t('text.numberPieceLinked', {
                                count: allNewPiecesLink_1.length * 10,
                                from: t("text.".concat(fromDenom)),
                                to: t("text.".concat(toDenom)),
                            }));
                        }
                    }
                    break;
                }
                case 'nmp_rotate_colored':
                case 'nmp_rotate': {
                    // Rotation is supported on:
                    //   - numberPiece tens   (1×10 grid)
                    //   - moneyPiece dime    (1×10 grid)
                    //   - moneyPiece nickel  (1×5 grid)
                    //   - moneyPiece halfDollar (5×10 grid)
                    // All four are non-square pieces. The grid swaps width↔height;
                    // any image inside (coin face) stays unrotated because the wrapper
                    // doesn't apply a CSS transform — it just changes box dimensions.
                    var isNpTens_1 = data.type === 'numberPiece' && data.denomination === 'tens';
                    var isRotatableMoney = data.type === 'moneyPiece' &&
                        (data.denomination === 'dime' ||
                            data.denomination === 'nickel' ||
                            data.denomination === 'halfDollar');
                    // GROUP CONTAINER ROTATE: NMP pieces never render inside
                    // ManipulativeGroup (see WhiteBoard.tsx manipulativeMerged — NMP
                    // pieces always stay standalone, using their own absolute `center`
                    // even when grouped; group-drag is coordinated separately via
                    // multiSelectMove events). So rotating the whole group 90° has to
                    // happen here: every grouped member independently receives this
                    // same broadcast 'nmp_rotate' event, and each one rotates its own
                    // `center` around the group's centroid using the same 2D rotation
                    // matrix as the on-canvas group rotate handle. Computing the
                    // centroid identically (avg of all live sibling centers) in every
                    // piece's closure means this is safe to do independently per piece
                    // — no single "owner" needs to coordinate it.
                    var groupRotatedCenter_1 = null;
                    if (data.groupId) {
                        var siblings = manipulative.filter(function (m) {
                            return (m.data.type === 'numberPiece' || m.data.type === 'moneyPiece') &&
                                m.data.groupId === data.groupId &&
                                !m.data.animatingOut;
                        });
                        if (siblings.length >= 2) {
                            var cx = siblings.reduce(function (s, m) { return s + m.data.center.x; }, 0) / siblings.length;
                            var cy = siblings.reduce(function (s, m) { return s + m.data.center.y; }, 0) / siblings.length;
                            var dx = data.center.x - cx;
                            var dy = data.center.y - cy;
                            // 90° clockwise rotation around the centroid: (dx, dy) -> (-dy, dx)
                            groupRotatedCenter_1 = { x: cx - dy, y: cy + dx };
                        }
                    }
                    if (!isNpTens_1 && !isRotatableMoney) {
                        // Square pieces (ones/hundreds/dollar/quarter/penny) can't swap
                        // their own dimensions, but they still participate in the group
                        // container rotation above.
                        if (groupRotatedCenter_1) {
                            saveState({
                                type: id,
                                id: id.split('_')[1],
                                propertyName: 'center',
                                old: JSON.stringify(data.center),
                                new: JSON.stringify(groupRotatedCenter_1),
                            });
                            setManipulative(function (prev) {
                                return prev.map(function (item) {
                                    return item.data.id === id
                                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: groupRotatedCenter_1 }) }) : item;
                                });
                            });
                        }
                        break;
                    }
                    // Default-dimension fallback per piece type (used only if data
                    // somehow lacks defaultDimensions — shouldn't happen for normal
                    // creation paths but kept defensive).
                    var fallbackDefault = (function () {
                        if (isNpTens_1)
                            return { width: GRID_SIZE, height: GRID_SIZE * 10 };
                        var md = data.denomination;
                        if (md === 'dime')
                            return { width: GRID_SIZE, height: GRID_SIZE * 10 };
                        if (md === 'nickel')
                            return { width: GRID_SIZE, height: GRID_SIZE * 5 };
                        return { width: GRID_SIZE * 5, height: GRID_SIZE * 10 }; // halfDollar
                    })();
                    if (data.groupId) {
                        // GROUPED: swap dimensions (so the piece's own footprint rotates
                        // 90°) AND apply the group container's center rotation computed
                        // above, in a single state update / undo step.
                        var oldDims = data.defaultDimensions || fallbackDefault;
                        var newDims_1 = { width: oldDims.height, height: oldDims.width };
                        var newCenter_1 = groupRotatedCenter_1 !== null && groupRotatedCenter_1 !== void 0 ? groupRotatedCenter_1 : data.center;
                        saveState({
                            type: id,
                            id: id.split('_')[1],
                            propertyName: 'completeData',
                            old: JSON.stringify({
                                defaultDimensions: oldDims,
                                dimensions: oldDims,
                                center: data.center,
                            }),
                            new: JSON.stringify({
                                defaultDimensions: newDims_1,
                                dimensions: newDims_1,
                                center: newCenter_1,
                            }),
                        });
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                return item.data.id === id
                                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { defaultDimensions: newDims_1, dimensions: newDims_1, center: newCenter_1 }) }) : item;
                            });
                        });
                    }
                    else {
                        // STANDALONE: swap defaultDimensions + dimensions so the piece
                        // renders natively as a horizontal row (via .numberPieceTens
                        // flex-row). Avoids the sub-pixel rendering drift that CSS
                        // `rotate(90deg)` introduces on bordered/outlined children, so
                        // cell gridlines align pixel-perfectly with a hundred's grid.
                        var oldDefault = data.defaultDimensions || fallbackDefault;
                        var oldCurrent = data.dimensions || oldDefault;
                        var oldRotation = data.rotation || 0;
                        var newDefault_1 = { width: oldDefault.height, height: oldDefault.width };
                        var newCurrent_1 = { width: oldCurrent.height, height: oldCurrent.width };
                        saveState({
                            type: id,
                            id: id.split('_')[1],
                            propertyName: 'completeData',
                            old: JSON.stringify({
                                defaultDimensions: oldDefault,
                                dimensions: oldCurrent,
                                rotation: oldRotation,
                            }),
                            new: JSON.stringify({
                                defaultDimensions: newDefault_1,
                                dimensions: newCurrent_1,
                                rotation: 0,
                            }),
                        });
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                return item.data.id === id
                                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { defaultDimensions: newDefault_1, dimensions: newCurrent_1, rotation: 0 }) }) : item;
                            });
                        });
                    }
                    // Announce orientation change. The piece toggles between vertical
                    // (height > width) and horizontal (width > height) after dim swap.
                    // Use the actual piece's denomination name (Tens / Dime / Nickel /
                    // Half Dollar) so the announcement matches what the user rotated —
                    // previously this always read "Tens piece rotated" regardless of
                    // type, which was confusing for money-piece bills.
                    {
                        var oldDef = data.defaultDimensions || fallbackDefault;
                        var wasVertical = oldDef.height > oldDef.width;
                        var denomKey = DENOMINATION_KEYS[data.denomination];
                        var denomName = denomKey ? t(denomKey) : data.denomination;
                        announce(t('text.nmpPieceRotated', {
                            name: denomName,
                            orientation: t(wasVertical ? 'text.horizontal' : 'text.vertical'),
                        }));
                    }
                    break;
                }
                case 'nmp_flip_money':
                case 'nmp_flip': {
                    if (data.type !== 'moneyPiece')
                        break;
                    // Only process once for the first selected money piece
                    var firstSelectedMoneyId = currentSelected.find(function (sid) { return sid.startsWith('moneyPiece_'); });
                    if (firstSelectedMoneyId !== id)
                        break;
                    // Use anyFlipped pattern: if any is not flipped, flip all; otherwise unflip all
                    var anyNotFlipped = currentSelected.some(function (sid) {
                        var m = manipulative.find(function (item) { return item.data.id === sid; });
                        return m && m.data.type === 'moneyPiece' && !m.data.isFlipped;
                    });
                    var newFlipped_1 = anyNotFlipped;
                    var flipStateChanges_1 = [];
                    currentSelected.forEach(function (sid) {
                        var _a;
                        var m = manipulative.find(function (item) { return item.data.id === sid; });
                        if (m && m.data.type === 'moneyPiece') {
                            flipStateChanges_1.push({
                                type: m.data.id,
                                id: m.data.id.split('_')[1],
                                propertyName: 'isFlipped',
                                old: JSON.stringify((_a = m.data.isFlipped) !== null && _a !== void 0 ? _a : false),
                                new: JSON.stringify(newFlipped_1),
                            });
                        }
                    });
                    saveState(flipStateChanges_1);
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            return currentSelected.includes(item.data.id) && item.data.type === 'moneyPiece'
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { isFlipped: newFlipped_1 }) }) : item;
                        });
                    });
                    // Announce flip side. Use this piece's denomination as the
                    // representative label (all selected coins flip together).
                    announce(t('text.moneyPieceFlipped', {
                        denomination: t("text.".concat(data.denomination)),
                        side: t(newFlipped_1 ? 'text.back' : 'text.front'),
                    }));
                    break;
                }
                case 'nmp_animation_yellow':
                case 'nmp_animation_colored':
                case 'nmp_animation_money':
                case 'nmp_animation': {
                    var firstSelectedNmpIdAnim = currentSelected.find(function (sid) { return sid.startsWith('numberPiece_') || sid.startsWith('moneyPiece_'); });
                    if (firstSelectedNmpIdAnim !== id)
                        break;
                    // Flip the single global NMP animation flag and apply it uniformly
                    // to every existing NMP piece on the canvas — not just the current
                    // selection — so the "once off, stays off" expectation holds across
                    // selection changes and later-created pieces.
                    var newAnimation_1 = !nmpAnimationEnabled;
                    var animStateChanges_1 = [];
                    manipulative.forEach(function (item) {
                        var _a, _b;
                        if (item.data.type !== 'numberPiece' && item.data.type !== 'moneyPiece')
                            return;
                        var mData = item.data;
                        if (((_a = mData.animation) !== null && _a !== void 0 ? _a : true) === newAnimation_1)
                            return;
                        animStateChanges_1.push({
                            type: mData.id,
                            id: mData.id.split('_')[1],
                            propertyName: 'animation',
                            old: JSON.stringify((_b = mData.animation) !== null && _b !== void 0 ? _b : true),
                            new: JSON.stringify(newAnimation_1),
                        });
                    });
                    if (animStateChanges_1.length > 0)
                        saveState(animStateChanges_1);
                    setNmpAnimationEnabled(newAnimation_1);
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.type !== 'numberPiece' && item.data.type !== 'moneyPiece')
                                return item;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { animation: newAnimation_1 }) });
                        });
                    });
                    break;
                }
                default: {
                    // Handle exchange events from ExchangePopup
                    if (toolData.id.startsWith('nmp_exchangeTo_')) {
                        var targetDenom = toolData.id.replace('nmp_exchangeTo_', '');
                        var EXCHANGE_VALUE_MAP_1 = {
                            dollar: 100,
                            halfDollar: 50,
                            quarter: 25,
                            dime: 10,
                            nickel: 5,
                            penny: 1,
                        };
                        // Collect all selected moneyPieces
                        var selectedMoney_3 = currentSelected
                            .map(function (sid) { return manipulative.find(function (m) { return m.data.id === sid; }); })
                            .filter(function (m) { return !!m && m.data.type === 'moneyPiece'; });
                        if (selectedMoney_3.length === 0)
                            break;
                        // Guard: first money piece handles exchange
                        // (must be a piece that gets animating:true so onAnimationEnd fires)
                        if (id !== selectedMoney_3[0].data.id)
                            break;
                        // Calculate total value in cents
                        var totalCents = selectedMoney_3.reduce(function (sum, m) {
                            return sum + EXCHANGE_VALUE_MAP_1[m.data.denomination];
                        }, 0);
                        var targetValue = EXCHANGE_VALUE_MAP_1[targetDenom];
                        var primaryCount = Math.floor(totalCents / targetValue);
                        var remainder = totalCents % targetValue;
                        if (primaryCount === 0 && remainder === 0)
                            break;
                        var firstPiece_1 = selectedMoney_3[0].data;
                        var pieceType_1 = firstPiece_1.pieceType;
                        var isCoin_1 = pieceType_1 === 'coins';
                        // Detect intended orientation for the exchange result. Only
                        // meaningful for non-coin pieces — money pieces (grid style) can
                        // be rotated; coin renders are round so orientation is irrelevant.
                        //
                        // Two cases:
                        //  - Multiple source pieces (e.g. 2 nickels → 1 dime, 5 pennies →
                        //    1 nickel): use the bounding box of the source centers. If
                        //    the user laid them out horizontally, the resulting larger
                        //    piece (when rotatable) should be horizontal too.
                        //  - Single source piece (e.g. 1 dollar → 100 pennies): inherit
                        //    that piece's own current orientation, so children fill the
                        //    parent's footprint in the same direction.
                        var parentDims = (_l = firstPiece_1.dimensions) !== null && _l !== void 0 ? _l : MONEY_PIECE_DIMENSIONS[firstPiece_1.denomination];
                        var parentIsHorizontal = !isCoin_1 && parentDims.width > parentDims.height;
                        // Total source footprint — the union of every source piece's
                        // rect. For a single source this equals its own dims; for
                        // multiple sources it's the bounding box that the children
                        // need to fill so the exchange preserves the original shape
                        // (e.g. 2 nickels side-by-side → 10 pennies in a 2×5 grid,
                        // not a 1×10 column). Using only `parentDims` here meant the
                        // child grid always sized off the FIRST source, which forced
                        // every multi-source exchange into a single-piece-wide column.
                        var footprintW_1 = parentDims.width;
                        var footprintH = parentDims.height;
                        if (!isCoin_1 && selectedMoney_3.length >= 2) {
                            var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
                            for (var _t = 0, selectedMoney_1 = selectedMoney_3; _t < selectedMoney_1.length; _t++) {
                                var m = selectedMoney_1[_t];
                                var md = m.data;
                                var dims_1 = (_m = md.dimensions) !== null && _m !== void 0 ? _m : MONEY_PIECE_DIMENSIONS[md.denomination];
                                var left = md.center.x - dims_1.width / 2;
                                var right = md.center.x + dims_1.width / 2;
                                var top_1 = md.center.y - dims_1.height / 2;
                                var bottom = md.center.y + dims_1.height / 2;
                                if (left < minX)
                                    minX = left;
                                if (right > maxX)
                                    maxX = right;
                                if (top_1 < minY)
                                    minY = top_1;
                                if (bottom > maxY)
                                    maxY = bottom;
                            }
                            footprintW_1 = Math.max(parentDims.width, maxX - minX);
                            footprintH = Math.max(parentDims.height, maxY - minY);
                            parentIsHorizontal = footprintW_1 > footprintH;
                        }
                        // Denominations that are non-square and can be visually rotated
                        // (their grid swaps cols/rows; coin overlay stays anchored).
                        var rotatableDenoms = new Set(['dime', 'nickel', 'halfDollar']);
                        // Dimension lookup per denomination + pieceType. For non-coin
                        // exchanges where the parent is horizontally oriented, rotate any
                        // rotatable child denom so children match the parent's footprint.
                        var exchangeDimMap_1 = {
                            dollar: isCoin_1 ? COIN_DIMENSIONS.dollar : MONEY_PIECE_DIMENSIONS.dollar,
                            halfDollar: isCoin_1 ? COIN_DIMENSIONS.halfDollar : MONEY_PIECE_DIMENSIONS.halfDollar,
                            quarter: isCoin_1 ? COIN_DIMENSIONS.quarter : MONEY_PIECE_DIMENSIONS.quarter,
                            dime: isCoin_1 ? COIN_DIMENSIONS.dime : MONEY_PIECE_DIMENSIONS.dime,
                            nickel: isCoin_1 ? COIN_DIMENSIONS.nickel : MONEY_PIECE_DIMENSIONS.nickel,
                            penny: isCoin_1 ? COIN_DIMENSIONS.penny : MONEY_PIECE_DIMENSIONS.penny,
                        };
                        if (parentIsHorizontal) {
                            for (var _u = 0, _v = Object.keys(exchangeDimMap_1); _u < _v.length; _u++) {
                                var d = _v[_u];
                                if (rotatableDenoms.has(d)) {
                                    var cur = exchangeDimMap_1[d];
                                    exchangeDimMap_1[d] = { width: cur.height, height: cur.width };
                                }
                            }
                        }
                        // Compute how many child pieces fit in a single row of the
                        // parent group's footprint (overrides the static layout.cols
                        // from constants). For non-coin: footprintW / childWidth — the
                        // footprint accounts for multi-source bounding boxes so a
                        // 2-wide source group exchanges to a 2-wide child grid. For
                        // coins or square parents (dollar / quarter / penny): keep
                        // the static layout — the visual expectation there is the
                        // existing pattern.
                        var computeLayoutCols_1 = function (denom, fallbackCols) {
                            if (isCoin_1)
                                return fallbackCols;
                            var childDims = exchangeDimMap_1[denom];
                            if (!childDims)
                                return fallbackCols;
                            var fit = Math.max(1, Math.floor(footprintW_1 / childDims.width));
                            return fit;
                        };
                        // Compute remainder by consuming source pieces for the target value
                        var remainderPieces = [];
                        var denomsDescending = [
                            'dollar',
                            'halfDollar',
                            'quarter',
                            'dime',
                            'nickel',
                            'penny',
                        ];
                        // Count selected pieces per denomination
                        var sourceCounts = {};
                        for (var _w = 0, selectedMoney_2 = selectedMoney_3; _w < selectedMoney_2.length; _w++) {
                            var m = selectedMoney_2[_w];
                            var d = m.data.denomination;
                            sourceCounts[d] = (sourceCounts[d] || 0) + 1;
                        }
                        // Consume pieces (largest first) to cover primaryCount × targetValue
                        var toConsume = primaryCount * targetValue;
                        for (var _x = 0, denomsDescending_1 = denomsDescending; _x < denomsDescending_1.length; _x++) {
                            var d = denomsDescending_1[_x];
                            if (!sourceCounts[d] || toConsume <= 0)
                                continue;
                            var val = EXCHANGE_VALUE_MAP_1[d];
                            var canConsume = Math.min(sourceCounts[d], Math.floor(toConsume / val));
                            sourceCounts[d] = sourceCounts[d] - canConsume;
                            toConsume -= canConsume * val;
                        }
                        // If toConsume > 0, a source piece is worth more than remaining need.
                        // Consume the smallest such piece and give greedy change for overpayment.
                        if (toConsume > 0) {
                            for (var _y = 0, _z = __spreadArray([], denomsDescending, true).reverse(); _y < _z.length; _y++) {
                                var d = _z[_y];
                                if (!sourceCounts[d] || sourceCounts[d] <= 0)
                                    continue;
                                var val = EXCHANGE_VALUE_MAP_1[d];
                                if (val >= toConsume) {
                                    sourceCounts[d] = sourceCounts[d] - 1;
                                    var overpayment = val - toConsume;
                                    for (var _0 = 0, denomsDescending_2 = denomsDescending; _0 < denomsDescending_2.length; _0++) {
                                        var cd = denomsDescending_2[_0];
                                        if (overpayment <= 0)
                                            break;
                                        var cv = EXCHANGE_VALUE_MAP_1[cd];
                                        var count = Math.floor(overpayment / cv);
                                        if (count > 0) {
                                            sourceCounts[cd] = (sourceCounts[cd] || 0) + count;
                                            overpayment -= count * cv;
                                        }
                                    }
                                    toConsume = 0;
                                    break;
                                }
                            }
                        }
                        // Whatever remains = remainder pieces
                        for (var _1 = 0, denomsDescending_3 = denomsDescending; _1 < denomsDescending_3.length; _1++) {
                            var d = denomsDescending_3[_1];
                            if (sourceCounts[d] && sourceCounts[d] > 0) {
                                remainderPieces.push({ denom: d, count: sourceCounts[d] });
                            }
                        }
                        // Grid positioning
                        var GAP_1 = 0;
                        var prefix_1 = 'moneyPiece';
                        var allNewPieces_2 = [];
                        // Helper to place pieces in a grid starting from a given top-left,
                        // optionally with a starting slot offset so a follow-up call can
                        // continue filling the same grid sequence (used to keep
                        // inline-eligible remainders on the same row as the primary
                        // pieces — e.g. quarter → 2 dimes + 1 nickel: nickel slots into
                        // the third column of the primary row instead of dropping below).
                        var placePieces = function (denom, count, startX, startY, cols, startSlot) {
                            if (startSlot === void 0) { startSlot = 0; }
                            var dims = exchangeDimMap_1[denom];
                            var layout = (isCoin_1 ? COIN_EXCHANGE_LAYOUT : MONEY_PIECE_EXCHANGE_LAYOUT)[denom];
                            // Override layout.cols so the children fit in the parent's
                            // footprint (matches the parent's orientation). See
                            // computeLayoutCols above.
                            var effectiveCols = cols !== null && cols !== void 0 ? cols : computeLayoutCols_1(denom, layout.cols);
                            var stepX = layout.overlapX > 0 ? dims.width * (1 - layout.overlapX) : dims.width + GAP_1;
                            var stepY = layout.overlapY > 0 ? dims.height * (1 - layout.overlapY) : dims.height + GAP_1;
                            for (var i = 0; i < count; i++) {
                                var slot = startSlot + i;
                                var col = slot % effectiveCols;
                                var row = Math.floor(slot / effectiveCols);
                                var cx_1 = startX + col * stepX + dims.width / 2;
                                var cy_1 = startY + row * stepY + dims.height / 2;
                                var newId_1 = "".concat(prefix_1, "_").concat(uuidv4().replace(/-/g, ''));
                                allNewPieces_2.push({
                                    type: prefix_1,
                                    widgetId: "".concat(prefix_1, ":").concat(uuidv4().replace(/-/g, '')),
                                    data: __assign({ id: newId_1, type: prefix_1, pieceType: pieceType_1, denomination: denom, animation: nmpAnimationEnabled, center: { x: cx_1, y: cy_1 }, dimensions: __assign({}, dims), defaultDimensions: __assign({}, dims), scale: 1, rotation: 0, isFlipped: false, lock: { positioning: false, editing: false } }, (firstPiece_1.groupId
                                        ? {
                                            groupId: firstPiece_1.groupId,
                                            groupScale: firstPiece_1.groupScale,
                                            beforeGroupScale: firstPiece_1.beforeGroupScale,
                                            groupCenter: firstPiece_1.groupCenter,
                                        }
                                        : {})),
                                });
                            }
                        };
                        // Per-source placement: each source piece's share of children
                        // gets placed inside (or extending from) that source's own
                        // footprint instead of pooling every source into a single
                        // bounding-box grid. Two separated dimes exchanged to nickels
                        // therefore produce 2 nickels in dime A's column AND 2 nickels
                        // in dime B's column, preserving the original gap — pooling
                        // would have packed all 4 nickels into a single contiguous
                        // block spanning the gap.
                        var primaryDims_1 = exchangeDimMap_1[targetDenom];
                        var primaryLayout = (isCoin_1 ? COIN_EXCHANGE_LAYOUT : MONEY_PIECE_EXCHANGE_LAYOUT)[targetDenom];
                        // Inline-eligible remainder = same WIDTH as primary, so it can
                        // slot into the leftover columns of the source's own primary
                        // row even if its height differs (top-aligned in the row).
                        // This is what makes quarter → 2 dimes + 1 nickel pack as a
                        // single row of three (dime, dime, nickel) within the
                        // quarter's footprint instead of orphaning the nickel below.
                        var isInlineEligible_1 = function (rpDenom) {
                            var rpDims = exchangeDimMap_1[rpDenom];
                            return rpDims.width === primaryDims_1.width;
                        };
                        // Sort sources by position (top-to-bottom, then left-to-right)
                        // so children render in a stable order across the canvas.
                        var sortedSources = __spreadArray([], selectedMoney_3, true).sort(function (a, b) {
                            var ad = a.data;
                            var bd = b.data;
                            if (Math.abs(ad.center.y - bd.center.y) > 1)
                                return ad.center.y - bd.center.y;
                            return ad.center.x - bd.center.x;
                        });
                        // Track unallocated counts; each source consumes from these as
                        // it accepts its share. Allocation is value-greedy: each
                        // source absorbs primary first (largest target denom), then
                        // remainder denoms in descending value order.
                        var remainingPrimary = primaryCount;
                        var remainingRemainder = new Map();
                        for (var _2 = 0, remainderPieces_1 = remainderPieces; _2 < remainderPieces_1.length; _2++) {
                            var rp = remainderPieces_1[_2];
                            remainingRemainder.set(rp.denom, ((_o = remainingRemainder.get(rp.denom)) !== null && _o !== void 0 ? _o : 0) + rp.count);
                        }
                        for (var _3 = 0, sortedSources_1 = sortedSources; _3 < sortedSources_1.length; _3++) {
                            var src = sortedSources_1[_3];
                            var srcData = src.data;
                            var srcDims = (_p = srcData.dimensions) !== null && _p !== void 0 ? _p : MONEY_PIECE_DIMENSIONS[srcData.denomination];
                            var valueLeft = EXCHANGE_VALUE_MAP_1[srcData.denomination];
                            // Source consumes its share of primary pieces first.
                            var primaryFromSrc = Math.min(remainingPrimary, Math.floor(valueLeft / targetValue));
                            remainingPrimary -= primaryFromSrc;
                            valueLeft -= primaryFromSrc * targetValue;
                            // Then the remainder denoms, largest first, capped by what's
                            // still globally available.
                            var remainderFromSrc = [];
                            for (var _4 = 0, denomsDescending_4 = denomsDescending; _4 < denomsDescending_4.length; _4++) {
                                var d = denomsDescending_4[_4];
                                var dVal = EXCHANGE_VALUE_MAP_1[d];
                                if (dVal > valueLeft)
                                    continue;
                                var remainingCount = (_q = remainingRemainder.get(d)) !== null && _q !== void 0 ? _q : 0;
                                if (remainingCount <= 0)
                                    continue;
                                var canTake = Math.min(remainingCount, Math.floor(valueLeft / dVal));
                                if (canTake <= 0)
                                    continue;
                                remainderFromSrc.push({ denom: d, count: canTake });
                                remainingRemainder.set(d, remainingCount - canTake);
                                valueLeft -= canTake * dVal;
                            }
                            var inlineSrcRemainders = remainderFromSrc.filter(function (rp) { return isInlineEligible_1(rp.denom); });
                            var belowSrcRemainders = remainderFromSrc.filter(function (rp) { return !isInlineEligible_1(rp.denom); });
                            var inlineRemainderCount = inlineSrcRemainders.reduce(function (s, rp) { return s + rp.count; }, 0);
                            var totalInlineCount = primaryFromSrc + inlineRemainderCount;
                            if (totalInlineCount === 0 && belowSrcRemainders.length === 0)
                                continue;
                            // Row width: how many primary-dim pieces fit across the
                            // source's own footprint. Falls back to the layout default
                            // for coins / square parents.
                            var srcLayoutCols = isCoin_1
                                ? primaryLayout.cols
                                : Math.max(1, Math.floor(srcDims.width / primaryDims_1.width));
                            var srcGridRows = totalInlineCount > 0 ? Math.ceil(totalInlineCount / srcLayoutCols) : 0;
                            var srcStepY = primaryLayout.overlapY > 0
                                ? primaryDims_1.height * (1 - primaryLayout.overlapY)
                                : primaryDims_1.height + GAP_1;
                            var srcGridH = srcGridRows * srcStepY - (primaryLayout.overlapY > 0 ? 0 : GAP_1);
                            // Anchor the children grid at the source's own footprint
                            // top-left so each source's exchange products sit where the
                            // source piece was. Clamp to canvas to avoid off-screen drift.
                            var rawStartX = srcData.center.x - srcDims.width / 2;
                            var rawStartY = srcData.center.y - srcDims.height / 2;
                            var srcGridStartX = Math.max(CANVAS_MARGIN, Math.min(rawStartX, canvasWidth - srcDims.width - CANVAS_MARGIN));
                            var srcGridStartY = Math.max(CANVAS_MARGIN, Math.min(rawStartY, canvasHeight - Math.max(srcGridH, srcDims.height) - CANVAS_MARGIN));
                            // Place primaries, then inline remainders continuing the row.
                            if (primaryFromSrc > 0) {
                                placePieces(targetDenom, primaryFromSrc, srcGridStartX, srcGridStartY, srcLayoutCols, 0);
                            }
                            var inlineSlot = primaryFromSrc;
                            for (var _5 = 0, inlineSrcRemainders_1 = inlineSrcRemainders; _5 < inlineSrcRemainders_1.length; _5++) {
                                var rp = inlineSrcRemainders_1[_5];
                                placePieces(rp.denom, rp.count, srcGridStartX, srcGridStartY, srcLayoutCols, inlineSlot);
                                inlineSlot += rp.count;
                            }
                            // Below-only remainders sit beneath this source's grid.
                            var remainderY = srcGridStartY + srcGridH + GAP_1 * 3;
                            for (var _6 = 0, belowSrcRemainders_1 = belowSrcRemainders; _6 < belowSrcRemainders_1.length; _6++) {
                                var rp = belowSrcRemainders_1[_6];
                                var rpDims = exchangeDimMap_1[rp.denom];
                                var rpLayout = (isCoin_1 ? COIN_EXCHANGE_LAYOUT : MONEY_PIECE_EXCHANGE_LAYOUT)[rp.denom];
                                var rpStepY = rpLayout.overlapY > 0
                                    ? rpDims.height * (1 - rpLayout.overlapY)
                                    : rpDims.height + GAP_1;
                                placePieces(rp.denom, rp.count, srcGridStartX, remainderY);
                                var rpEffectiveCols = computeLayoutCols_1(rp.denom, rpLayout.cols);
                                var rpRows = Math.ceil(rp.count / rpEffectiveCols);
                                remainderY += rpRows * rpStepY + GAP_1 * 2;
                            }
                        }
                        // Fallback: place any primary pieces that no individual source could
                        // afford (e.g., 100 pennies → 1 dollar — each penny is worth 1¢ but
                        // the dollar costs 100¢, so per-source allocation yields 0 per source).
                        if (remainingPrimary > 0) {
                            var sumX = sortedSources.reduce(function (s, src) { return s + src.data.center.x; }, 0);
                            var sumY = sortedSources.reduce(function (s, src) { return s + src.data.center.y; }, 0);
                            var centroidX = sumX / sortedSources.length;
                            var centroidY = sumY / sortedSources.length;
                            var startX = Math.max(CANVAS_MARGIN, Math.min(centroidX - primaryDims_1.width / 2, canvasWidth - primaryDims_1.width - CANVAS_MARGIN));
                            var startY = Math.max(CANVAS_MARGIN, Math.min(centroidY - primaryDims_1.height / 2, canvasHeight - primaryDims_1.height - CANVAS_MARGIN));
                            placePieces(targetDenom, remainingPrimary, startX, startY, primaryLayout.cols, 0);
                        }
                        // Re-index groupPosition for exchange pieces staying in group
                        if (firstPiece_1.groupId && allNewPieces_2.length > 0) {
                            var grpId_3 = firstPiece_1.groupId;
                            var existingMembers = manipulative
                                .filter(function (m) {
                                return m.data.groupId === grpId_3 && !selectedMoney_3.some(function (s) { return s.data.id === m.data.id; });
                            })
                                .sort(function (a, b) { var _a, _b; return ((_a = a.data.groupPosition) !== null && _a !== void 0 ? _a : 0) - ((_b = b.data.groupPosition) !== null && _b !== void 0 ? _b : 0); });
                            var pos_3 = 0;
                            existingMembers.forEach(function (m) {
                                m.data.groupPosition = pos_3++;
                            });
                            allNewPieces_2.forEach(function (p) {
                                p.data.groupPosition = pos_3++;
                            });
                        }
                        // Build state changes: delete all selected + batchCreate new
                        var exchangeStateChanges_1 = [];
                        selectedMoney_3.forEach(function (item) {
                            exchangeStateChanges_1.push({
                                type: item.data.id,
                                id: item.data.id,
                                propertyName: 'delete',
                                old: JSON.stringify(item),
                            });
                        });
                        exchangeStateChanges_1.push({
                            type: allNewPieces_2[0].data.id,
                            id: allNewPieces_2[0].data.id.split('_')[1],
                            propertyName: 'batchCreate',
                            old: JSON.stringify(allNewPieces_2),
                        });
                        var deleteIds_2 = selectedMoney_3.map(function (m) { return m.data.id; });
                        var exchangeNewPieceIds = allNewPieces_2.map(function (p) { return p.data.id; });
                        // Check if all selected pieces have animation enabled
                        var allExchangeAnimationOn = selectedMoney_3.every(function (m) { return m.data.animation !== false; });
                        // Defensive: never commit an exchange that produces nothing —
                        // otherwise the source coins get deleted without replacement
                        // ("coins vanished after exchange" report).
                        if (allNewPieces_2.length === 0)
                            break;
                        // Compose the screen-reader announcement once. Uses the values
                        // already computed above: `firstPiece.denomination`, `targetDenom`,
                        // `primaryCount`, `remainderPieces`. The announcement fires after
                        // the new pieces are committed (animated or instant path below).
                        var denomLabel_1 = function (d, count) {
                            var key = DENOMINATION_KEYS[d];
                            var denomName = key ? t(key) : d;
                            return "".concat(count, " ").concat(denomName);
                        };
                        var fromKey = DENOMINATION_KEYS[firstPiece_1.denomination];
                        var fromName = fromKey ? t(fromKey) : firstPiece_1.denomination;
                        var primaryStr = denomLabel_1(targetDenom, primaryCount);
                        var remainderStrs = remainderPieces.map(function (rp) { return denomLabel_1(rp.denom, rp.count); });
                        var exchangeAnnouncement_1 = remainderStrs.length === 0
                            ? t('text.nmpExchanged', { from: fromName, primary: primaryStr })
                            : t('text.nmpExchangedWithRemainder', {
                                from: fromName,
                                primary: primaryStr,
                                remainder: remainderStrs.join(" ".concat(t('text.and'), " ")),
                            });
                        if (allExchangeAnimationOn) {
                            // === ANIMATED EXCHANGE ===
                            // t=0: Mark old pieces animatingOut → fade-out starts; ticker → buttons disable
                            nmpAnimationTicker.setAnimating(true);
                            setManipulative(function (prev) {
                                return prev.map(function (m) {
                                    return deleteIds_2.includes(m.data.id)
                                        ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { animatingOut: true }) }) : m;
                                });
                            });
                            // t=1s: Add new pieces — they read ticker at mount (isAnimating=true) → fade in.
                            // Spliced in just BEFORE the still-present (animatingOut) old
                            // pieces so they keep rendering on top during the fade, via
                            // array position alone (no zIndex needed).
                            setTimeout(function () {
                                var _loop_7 = function (start) {
                                    var chunk = allNewPieces_2.slice(start, start + 50);
                                    requestAnimationFrame(function () {
                                        setManipulative(function (prev) {
                                            var firstOldIdx = prev.findIndex(function (m) {
                                                return deleteIds_2.includes(m.data.id);
                                            });
                                            var insertAt = firstOldIdx === -1 ? prev.length : firstOldIdx;
                                            var next = __spreadArray([], prev, true);
                                            next.splice.apply(next, __spreadArray([insertAt, 0], chunk, false));
                                            return next;
                                        });
                                    });
                                };
                                for (var start = 0; start < allNewPieces_2.length; start += 50) {
                                    _loop_7(start);
                                }
                            }, 1000);
                            // t=3.1s: Remove old pieces, save state, update selection; ticker → buttons enable
                            setTimeout(function () {
                                var deleteIdSet = new Set(deleteIds_2);
                                var exchangeNewPieceIds = allNewPieces_2.map(function (p) { return p.data.id; });
                                setManipulative(function (prev) {
                                    return bringManipulativesToFront(prev
                                        .filter(function (m) { return !deleteIdSet.has(m.data.id); })
                                        .map(function (m) {
                                        return m.data.animatingOut
                                            ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { animatingOut: undefined }) }) : m;
                                    }), exchangeNewPieceIds);
                                });
                                nmpAnimationTicker.setAnimating(false);
                                saveState(exchangeStateChanges_1);
                                setSelectedTool(firstPiece_1.groupId ? [firstPiece_1.groupId] : []);
                                announce(exchangeAnnouncement_1);
                            }, 3100);
                            playAudio('exchangeNMP');
                        }
                        else {
                            // === INSTANT EXCHANGE ===
                            // Bail if the exchange produced no new pieces — otherwise we
                            // would delete the source coins without replacing them (the
                            // "coins vanished after exchange" report). This is defensive;
                            // `primaryCount === 0 && remainder === 0` already short-circuits
                            // above, but additional upstream logic changes could leave
                            // allNewPieces empty while deleteIds is not.
                            if (allNewPieces_2.length === 0)
                                break;
                            saveState(exchangeStateChanges_1);
                            var deleteIdSet_1 = new Set(deleteIds_2);
                            // Atomic commit: filter out old pieces AND insert the new ones
                            // in a single state update so React never renders an intermediate
                            // frame where the source coins are gone and their replacement
                            // hasn't landed yet. Small merges (e.g. 2 nickels → 1 dime) are
                            // committed in one pass; large exchanges (e.g. dollar → 100
                            // pennies) still chunk the REMAINING inserts across rAF frames.
                            var FIRST_BATCH = Math.min(allNewPieces_2.length, 50);
                            var firstBatch_1 = allNewPieces_2.slice(0, FIRST_BATCH);
                            var rest = allNewPieces_2.slice(FIRST_BATCH);
                            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return !deleteIdSet_1.has(m.data.id); }), true), firstBatch_1, true); });
                            if (rest.length === 0) {
                                setSelectedTool(firstPiece_1.groupId ? [firstPiece_1.groupId] : []);
                            }
                            else {
                                var _loop_4 = function (start) {
                                    var chunk = rest.slice(start, start + 50);
                                    var isLast = start + 50 >= rest.length;
                                    requestAnimationFrame(function () {
                                        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), chunk, true); });
                                        if (isLast) {
                                            setSelectedTool(firstPiece_1.groupId ? [firstPiece_1.groupId] : []);
                                        }
                                    });
                                };
                                for (var start = 0; start < rest.length; start += 50) {
                                    _loop_4(start);
                                }
                            }
                            announce(exchangeAnnouncement_1);
                            playAudio('exchangeNMP');
                        }
                        break;
                    }
                    if (!toolData.id.startsWith('nmp_'))
                        break;
                    // Only the first-selected NMP should create the new piece
                    var firstSelectedNmpIdCreate = currentSelected.find(function (sid) { return sid.startsWith('numberPiece_') || sid.startsWith('moneyPiece_'); });
                    if (firstSelectedNmpIdCreate !== id)
                        break;
                    var isNumber = data.type === 'numberPiece';
                    var selectedData = data;
                    var denomination = DENOM_BTN_MAP[toolData.id];
                    if (!denomination)
                        break;
                    var isCoin_2 = selectedData.pieceType === 'coins';
                    var dimMap = __assign(__assign({}, PIECE_DIMENSIONS), { dollar: isNumber
                            ? PIECE_DIMENSIONS.ones
                            : isCoin_2
                                ? COIN_DIMENSIONS.dollar
                                : MONEY_PIECE_DIMENSIONS.dollar, halfDollar: isCoin_2 ? COIN_DIMENSIONS.halfDollar : MONEY_PIECE_DIMENSIONS.halfDollar, quarter: isCoin_2 ? COIN_DIMENSIONS.quarter : MONEY_PIECE_DIMENSIONS.quarter, dime: isCoin_2 ? COIN_DIMENSIONS.dime : MONEY_PIECE_DIMENSIONS.dime, nickel: isCoin_2 ? COIN_DIMENSIONS.nickel : MONEY_PIECE_DIMENSIONS.nickel, penny: isCoin_2 ? COIN_DIMENSIONS.penny : MONEY_PIECE_DIMENSIONS.penny });
                    var dims = dimMap[denomination] || PIECE_DIMENSIONS.ones;
                    var cx = 0, cy = 0;
                    if (toolData.center) {
                        cx = toolData.center.x;
                        cy = toolData.center.y;
                    }
                    else {
                        var el = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
                        if (el) {
                            var z = zoomLevel || 1;
                            cy = el.scrollTop / z + el.clientHeight / z / 2;
                            cx = el.scrollLeft / z + el.clientWidth / z / 2;
                        }
                    }
                    var prefix = isNumber ? 'numberPiece' : 'moneyPiece';
                    var newId = "".concat(prefix, "_").concat(uuidv4().replace(/-/g, ''));
                    // Build temporary piece data to compute proper snap dimensions
                    // Use the current piece's scale so new pieces match the selected piece's size
                    var newPieceScale = (_r = data.scale) !== null && _r !== void 0 ? _r : 1;
                    var tempPiece = {
                        type: prefix,
                        pieceType: selectedData.pieceType,
                        denomination: denomination,
                        scale: newPieceScale,
                        rotation: 0,
                    };
                    var snapDims = getSnapDims(tempPiece);
                    // Snap new piece to grid or nearby pieces using render dimensions
                    var snapped = snapNMPPosition(cx, cy, snapDims.w, snapDims.h);
                    cx = snapped.x;
                    cy = snapped.y;
                    // For coins snapNMPPosition returns the position unchanged, so scan
                    // outward to find a free spot (mirrors the avoidOverlap logic in
                    // createNmpPieceAt used by the no-selection path).
                    if (isCoin_2) {
                        var BUFFER_1 = GRID_SIZE;
                        var cw_1 = snapDims.w;
                        var ch_1 = snapDims.h;
                        var sameType_1 = manipulative.filter(function (m) { return m.data.type === 'numberPiece' || m.data.type === 'moneyPiece'; });
                        var overlapsAt = function (tx, ty) {
                            var myL = tx - cw_1 / 2;
                            var myR = tx + cw_1 / 2;
                            var myT = ty - ch_1 / 2;
                            var myB = ty + ch_1 / 2;
                            for (var _i = 0, sameType_2 = sameType_1; _i < sameType_2.length; _i++) {
                                var other = sameType_2[_i];
                                var od = other.data;
                                var od2 = getSnapDims(od);
                                if (myL < od.center.x + od2.w / 2 + BUFFER_1 &&
                                    myR > od.center.x - od2.w / 2 - BUFFER_1 &&
                                    myT < od.center.y + od2.h / 2 + BUFFER_1 &&
                                    myB > od.center.y - od2.h / 2 - BUFFER_1)
                                    return true;
                            }
                            return false;
                        };
                        if (sameType_1.length > 0 && overlapsAt(cx, cy)) {
                            var stepX = cw_1 + BUFFER_1 + 1;
                            var stepY = ch_1 + BUFFER_1 + 1;
                            outer: for (var ring = 1; ring <= 40; ring++) {
                                var rim = [];
                                for (var dy = -ring; dy <= ring; dy++)
                                    rim.push({ dx: ring, dy: dy });
                                for (var dx = ring - 1; dx >= -ring; dx--)
                                    rim.push({ dx: dx, dy: ring });
                                for (var dy = ring - 1; dy >= -ring; dy--)
                                    rim.push({ dx: -ring, dy: dy });
                                for (var dx = -ring + 1; dx <= ring - 1; dx++)
                                    rim.push({ dx: dx, dy: -ring });
                                for (var _7 = 0, rim_1 = rim; _7 < rim_1.length; _7++) {
                                    var _8 = rim_1[_7], dx = _8.dx, dy = _8.dy;
                                    var tryX = cx + dx * stepX;
                                    var tryY = cy + dy * stepY;
                                    if (!overlapsAt(tryX, tryY)) {
                                        cx = tryX;
                                        cy = tryY;
                                        break outer;
                                    }
                                }
                            }
                        }
                    }
                    // Clamp to canvas bounds (no margin — allow placement at edge)
                    cx = Math.max(snapDims.w / 2, Math.min(cx, canvasWidth - snapDims.w / 2));
                    cy = Math.max(snapDims.h / 2, Math.min(cy, canvasHeight - snapDims.h / 2));
                    // New piece is appended to the array end below (`[...prev, newItem]`),
                    // which already renders it frontmost — no zIndex needed.
                    var pieceData = {
                        id: newId,
                        type: prefix,
                        pieceType: selectedData.pieceType,
                        denomination: denomination,
                        animation: nmpAnimationEnabled,
                        color: isNumber ? selectedData.color : undefined,
                        center: { x: cx, y: cy },
                        dimensions: __assign({}, dims),
                        defaultDimensions: __assign({}, dims),
                        scale: newPieceScale,
                        rotation: 0,
                        lock: { positioning: false, editing: false },
                    };
                    if (!isNumber) {
                        pieceData.isFlipped = false;
                    }
                    var newItem_1 = {
                        type: prefix,
                        widgetId: "".concat(prefix, ":").concat(uuidv4().replace(/-/g, '')),
                        data: pieceData,
                    };
                    saveState({
                        type: newId,
                        id: newId.split('_')[1],
                        propertyName: 'create',
                        old: JSON.stringify(newItem_1),
                    });
                    setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newItem_1], false); });
                    setSelectedTool([newId]);
                    break;
                }
            }
        };
        var changeColour = function (eventData) {
            if (data.type !== 'numberPiece')
                return;
            var currentItem = manipulative.find(function (item) { return item.data.id === id; });
            if (!currentItem)
                return;
            var npData = currentItem.data;
            var oldColor = npData.color;
            var oldPieceType = npData.pieceType;
            // Single undo step for both color and pieceType
            saveState([
                {
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'color',
                    old: JSON.stringify(oldColor !== null && oldColor !== void 0 ? oldColor : null),
                    new: JSON.stringify(eventData.color),
                },
                {
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'pieceType',
                    old: JSON.stringify(oldPieceType),
                    new: JSON.stringify('numberColored'),
                },
            ]);
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { color: eventData.color, pieceType: 'numberColored' }) })
                        : item;
                });
            });
            // Force selectedTool refresh so RightSideToolPanel updates rightConfig for new pieceType
            setSelectedTool(__spreadArray([], selectedToolRef.current, true));
        };
        if (isFocused) {
            customEventHandler.addEventListener('bottomPanel', onToolClick);
            customEventHandler.addEventListener('colourChange', changeColour);
        }
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
            customEventHandler.removeEventListener('colourChange', changeColour);
        };
    }, [isFocused, pieceScale, manipulative]);
    var renderPiece = function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        var isNumberType = data.type === 'numberPiece';
        var focusStyle = isFocused && !isGroup && !flipAnimating
            ? { boxShadow: "0 0 ".concat(1.25 * pieceScale, "rem 0 #5063AE") }
            : {};
        if (isNumberType) {
            var npData = data;
            var colorMap = NUMBER_COLORS[npData.pieceType] || NUMBER_COLORS['numberYellow'];
            var bgColor_1 = npData.color || colorMap[npData.denomination];
            switch (npData.denomination) {
                case 'ones':
                    return (_jsx("div", { className: "numberPieceCell onesCell", style: __assign({ background: bgColor_1 }, focusStyle), children: _jsx("div", { className: "nmpBorderOverlay" }) }));
                case 'tens': {
                    // "Rotated" tens have swapped dims (width > height) — render as row
                    // Check defaultDimensions directly (works for both grouped and standalone with groupId)
                    var isHorizontalTens_1 = ((_b = (_a = data.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : GRID_SIZE) >
                        ((_d = (_c = data.defaultDimensions) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : GRID_SIZE * 10);
                    return (_jsxs("div", { className: "numberPieceTens", style: __assign(__assign({}, focusStyle), (isHorizontalTens_1 ? { flexDirection: 'row' } : {})), children: [Array.from({ length: 10 }).map(function (_, i) { return (_jsx("div", { className: "numberPieceCell", style: __assign({ background: bgColor_1 }, (isHorizontalTens_1
                                    ? {
                                        borderBottom: 'none',
                                        borderRight: i < 9 ? '0.0625rem solid var(--internal-border-color)' : 'none',
                                    }
                                    : {})) }, i)); }), _jsx("div", { className: "nmpBorderOverlay" })] }));
                }
                case 'hundreds':
                    return (_jsxs("div", { className: "numberPieceHundreds", style: focusStyle, children: [Array.from({ length: 100 }).map(function (_, i) { return (_jsx("div", { className: "numberPieceCell", style: { background: bgColor_1 } }, i)); }), _jsx("div", { className: "nmpBorderOverlay" })] }));
            }
        }
        else {
            var mpData_1 = data;
            var frontSrc = CURRENCY_IMAGES[mpData_1.denomination].front;
            var backSrc = CURRENCY_IMAGES[mpData_1.denomination].back;
            var isFlipped = (_e = mpData_1.isFlipped) !== null && _e !== void 0 ? _e : false;
            // Uniform flip animation: if ANY selected peer has animation off, no animation for all
            var animateFlip = (function () {
                if (mpData_1.animation === false)
                    return false;
                var peerIds = selectedTool.length === 1 && selectedTool[0].startsWith('manipulativeGroup_')
                    ? manipulative
                        .filter(function (m) { return m.data.groupId === selectedTool[0]; })
                        .map(function (m) { return m.data.id; })
                    : selectedTool;
                if (peerIds.length > 1) {
                    return !peerIds.some(function (sid) {
                        var m = manipulative.find(function (item) { return item.data.id === sid; });
                        return m && m.data.animation === false;
                    });
                }
                return true;
            })();
            if (mpData_1.pieceType === 'coins') {
                var isRound = mpData_1.denomination !== 'dollar';
                var coinFocusStyle = isFocused && !isGroup && !flipAnimating
                    ? __assign(__assign({}, focusStyle), { borderRadius: isRound ? '50%' : undefined, border: "".concat(2 * pieceScale, "px solid #5063AE") }) : {
                    borderRadius: isRound ? '50%' : undefined,
                    border: !isRound
                        ? "".concat(2 * pieceScale, "px solid #5063AE")
                        : "".concat(2 * pieceScale, "px solid transparent"),
                };
                return (_jsx("div", { className: "coinPiece coinPiece--".concat(mpData_1.denomination === 'dollar' ? 'dollarBill' : mpData_1.denomination), style: isGroup
                        ? {
                            width: ((_g = (_f = data.defaultDimensions) === null || _f === void 0 ? void 0 : _f.width) !== null && _g !== void 0 ? _g : 0) *
                                ((_h = data.beforeGroupScale) !== null && _h !== void 0 ? _h : 1) *
                                ((_j = data.groupScale) !== null && _j !== void 0 ? _j : 1),
                            height: ((_l = (_k = data.defaultDimensions) === null || _k === void 0 ? void 0 : _k.height) !== null && _l !== void 0 ? _l : 0) *
                                ((_m = data.beforeGroupScale) !== null && _m !== void 0 ? _m : 1) *
                                ((_o = data.groupScale) !== null && _o !== void 0 ? _o : 1),
                        }
                        : {
                            width: ((_q = (_p = data.dimensions) === null || _p === void 0 ? void 0 : _p.width) !== null && _q !== void 0 ? _q : 0) * pieceScale,
                            height: ((_s = (_r = data.dimensions) === null || _r === void 0 ? void 0 : _r.height) !== null && _s !== void 0 ? _s : 0) * pieceScale,
                        }, children: _jsxs("div", { className: "nmpFlipInner".concat(isFlipped ? ' nmpFlipped' : '').concat(animateFlip ? ' nmpFlipAnimated' : ''), onTransitionStart: function () { return setFlipAnimating(true); }, onTransitionEnd: function () { return setFlipAnimating(false); }, children: [_jsx("div", { className: "nmpFlipFront", children: _jsx("img", { src: frontSrc, alt: "", "aria-hidden": "true", className: "coinImage", draggable: false, style: coinFocusStyle }) }), _jsx("div", { className: "nmpFlipBack", children: _jsx("img", { src: backSrc, alt: "", "aria-hidden": "true", className: "coinImage", draggable: false, style: coinFocusStyle }) })] }) }));
            }
            else {
                // Derive cols/rows from the CURRENT dimensions so rotated dime / nickel
                // / halfDollar render with their grid swapped (e.g. dime 1×10 ⇄ 10×1).
                // Falls back to the default grid for non-rotatable pieces.
                var nativeGrid = MONEY_GRIDS[mpData_1.denomination];
                var dims = (_t = data.dimensions) !== null && _t !== void 0 ? _t : MONEY_PIECE_DIMENSIONS[mpData_1.denomination];
                var widthCells = Math.max(1, Math.round(dims.width / PIECE_GRID));
                var heightCells = Math.max(1, Math.round(dims.height / PIECE_GRID));
                // Only swap if the current dims actually differ from the native grid
                // shape (avoids any rounding surprises on non-rotatable pieces).
                var isRotated = widthCells === nativeGrid.rows &&
                    heightCells === nativeGrid.cols &&
                    nativeGrid.cols !== nativeGrid.rows;
                var cols_1 = isRotated ? nativeGrid.rows : nativeGrid.cols;
                var rows_1 = isRotated ? nativeGrid.cols : nativeGrid.rows;
                // Inline pixel sizes override the per-denomination CSS hardcodes
                // (`.moneyPieceGrid--halfDollar` etc.) so a rotated piece's box
                // matches the swapped grid. cellPx accounts for the current scale;
                // CSS variable --nmpCellSize is also kept for cell rendering.
                var cellPx = PIECE_GRID * pieceScale;
                var widthPx = cols_1 * cellPx;
                var heightPx = rows_1 * cellPx;
                // Coin overlay width in CELL units — derived from the original CSS
                // percentages (e.g. halfDollar was 31% of a 5-cell-wide grid =
                // 1.55 cells; dime/nickel were 89% of a 1-cell-wide grid = 0.89
                // cells). This is a fixed coin size regardless of orientation.
                var coinCellWidth = {
                    dime: 0.89,
                    nickel: 0.89,
                    halfDollar: 1.55,
                    dollar: 0,
                    quarter: 0,
                    penny: 0,
                };
                var overlayWidthPx = coinCellWidth[mpData_1.denomination] > 0
                    ? coinCellWidth[mpData_1.denomination] * cellPx
                    : undefined;
                var gridStyle = {
                    gridTemplateColumns: "repeat(".concat(cols_1, ", var(--nmpCellSize, ").concat(PIECE_GRID, "px))"),
                    gridTemplateRows: "repeat(".concat(rows_1, ", var(--nmpCellSize, ").concat(PIECE_GRID, "px))"),
                    width: widthPx,
                    height: heightPx,
                };
                var wrapperBoxStyle = __assign(__assign({}, focusStyle), { width: widthPx, height: heightPx });
                var overlayStyle = overlayWidthPx !== undefined ? { width: overlayWidthPx } : undefined;
                // Compute border-right / border-bottom per cell from the ACTUAL cols/
                // rows so rotation doesn't mis-drop internal borders. The SCSS
                // `:nth-child(5n)` rules (e.g. `.moneyPieceGrid--halfDollar`) are
                // anchored to the un-rotated layout — overriding here keeps lines
                // correct in both orientations. Last column / last row drop their
                // borders so the wrapper's outline draws the perimeter.
                var lineCss_1 = mpData_1.denomination === 'dollar'
                    ? 'var(--internal-line-width) solid #61b995'
                    : 'var(--internal-line-width) solid var(--internal-border-color)';
                var gridCells = Array.from({ length: cols_1 * rows_1 }).map(function (_, i) {
                    var colIdx = i % cols_1;
                    var rowIdx = Math.floor(i / cols_1);
                    var isLastCol = colIdx === cols_1 - 1;
                    var isLastRow = rowIdx === rows_1 - 1;
                    var cellStyle = {
                        borderRight: isLastCol ? 'none' : lineCss_1,
                        borderBottom: isLastRow ? 'none' : lineCss_1,
                    };
                    return _jsx("div", { className: "moneyPieceGridCell", style: cellStyle }, i);
                });
                return (_jsx("div", { className: "moneyPieceWrapper", style: wrapperBoxStyle, children: _jsxs("div", { className: "nmpFlipInner".concat(isFlipped ? ' nmpFlipped' : '').concat(animateFlip ? ' nmpFlipAnimated' : ''), onTransitionStart: function () { return setFlipAnimating(true); }, onTransitionEnd: function () { return setFlipAnimating(false); }, children: [_jsxs("div", { className: "nmpFlipFront", children: [_jsxs("div", { className: "moneyPieceGrid moneyPieceGrid--".concat(mpData_1.denomination), style: gridStyle, children: [gridCells, _jsx("div", { className: "nmpBorderOverlay" })] }), _jsx("div", { className: "moneyPieceOverlay moneyPieceOverlay--".concat(mpData_1.denomination), style: overlayStyle, children: _jsx("img", { src: frontSrc, alt: "", "aria-hidden": "true", className: "moneyOverlayImage", draggable: false }) })] }), _jsxs("div", { className: "nmpFlipBack", children: [_jsxs("div", { className: "moneyPieceGrid moneyPieceGrid--".concat(mpData_1.denomination), style: gridStyle, children: [gridCells, _jsx("div", { className: "nmpBorderOverlay" })] }), _jsx("div", { className: "moneyPieceOverlay moneyPieceOverlay--".concat(mpData_1.denomination), style: overlayStyle, children: _jsx("img", { src: backSrc, alt: "", "aria-hidden": "true", className: "moneyOverlayImage", draggable: false }) })] })] }) }));
            }
        }
    };
    var isAnimatingOut = !!data.animatingOut;
    var isNmpSnapped = !!data.snappedId &&
        manipulative.some(function (m) {
            return m.data.id !== id &&
                (m.data.type === 'numberPiece' || m.data.type === 'moneyPiece') &&
                m.data.snappedId === data.snappedId;
        });
    return (_jsxs("div", __assign({ ref: wrapperRef, className: "numberAndMoneyPieceWrapper".concat(breakPhase === 'in' ? ' nmpBreakIn' : '').concat(isNmpSnapped ? ' nmpSnapped' : ''), "data-nmp-id": id, "data-manipulative-id": id }, (data.groupId ? { 'data-group-id': data.groupId } : {}), { style: __assign(__assign(__assign(__assign({ '--internal-line-width': zoomLevel <= 0.5 ? '0.125rem' : '0.0625rem', '--snapped-external-border-width': zoomLevel <= 0.5 ? '6px' : '3px', position: isGroup ? 'relative' : 'absolute', top: isGroup ? 0 : dragStated.current ? position.y : data.center.y, left: isGroup ? 0 : dragStated.current ? position.x : data.center.x }, (isGroup
            ? {
                width: ((_h = (_g = data.defaultDimensions) === null || _g === void 0 ? void 0 : _g.width) !== null && _h !== void 0 ? _h : 0) *
                    ((_j = data.beforeGroupScale) !== null && _j !== void 0 ? _j : 1) *
                    ((_k = data.groupScale) !== null && _k !== void 0 ? _k : 1),
                height: ((_m = (_l = data.defaultDimensions) === null || _l === void 0 ? void 0 : _l.height) !== null && _m !== void 0 ? _m : 0) *
                    ((_o = data.beforeGroupScale) !== null && _o !== void 0 ? _o : 1) *
                    ((_p = data.groupScale) !== null && _p !== void 0 ? _p : 1),
                '--nmpCellSize': "".concat(PIECE_GRID * ((_q = data.beforeGroupScale) !== null && _q !== void 0 ? _q : 1) * ((_r = data.groupScale) !== null && _r !== void 0 ? _r : 1), "px"),
            }
            : {
                '--nmpCellSize': "".concat(PIECE_GRID * pieceScale, "px"),
            })), { transform: isGroup
                ? "rotate(".concat(data.rotation || 0, "deg)")
                : "translate(-50%, -50%) rotate(".concat(data.rotation || 0, "deg)"), transformOrigin: 'center', 
            // CSS-only bring-to-front while actively dragging — permanent
            // stacking comes from `manipulative` array position (reordered on
            // drop via getReorderStateChanges). Grouped pieces leave zIndex
            // undefined; the shared ManipulativeGroup wrapper handles their
            // stacking.
            zIndex: isGroup ? undefined : isDragging ? 9999 : undefined }), (isAnimatingOut
            ? {
                opacity: fadeOutOpacity,
                transition: 'opacity 2s ease-in',
                pointerEvents: 'none',
            }
            : breakPhase === 'in'
                ? { opacity: fadeInOpacity }
                : {})), (isGroup ? { pointerEvents: 'none' } : {})), children: [_jsx(ButtonComponent, { defaultCSS: "nmpDragOverlay ".concat(isDragging ? 'movingObject' : '', " ").concat(positionLock ? 'nmpDragOverlay_locked' : ''), normalCSS: "nmpDragOverlay", avoidDefaultStyle: true, onMouseDown: onMouseDown, onTouchStart: onMouseDown, clickHandler: function (e) {
                    var _a;
                    if (!isGroup && !selectionAppliedRef.current) {
                        var targetId = (_a = data.groupId) !== null && _a !== void 0 ? _a : id;
                        setSelectedTool(selectTool(e, targetId, selectedTool, false));
                    }
                }, ariaLabelTranslated: duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : buildPieceLabel(), ariaRoledescription: data.type === 'moneyPiece' ? t('text.moneyPieceRole') : t('text.numberPieceRole'), 
                // Announce the available keyboard affordances so screen readers
                // surface the shortcut set when the piece gets focus.
                ariaKeyShortcuts: ((_s = data.lock) === null || _s === void 0 ? void 0 : _s.editing)
                    ? 'Enter Shift+F10 ContextMenu'
                    : 'Enter Shift+F10 ContextMenu ArrowUp ArrowDown ArrowLeft ArrowRight Delete Backspace', 
                // When grouped, yield Tab to NumberAndMoneyPieceSelectionGroup —
                // the overlay is the canonical single tab stop for the group, and
                // every group member is reached via that one stop.
                tabIndex: isGroup || ((_t = data.lock) === null || _t === void 0 ? void 0 : _t.editing) || data.groupId ? -1 : 0, onKeyDown: handleKeyDown, onFocus: function (e) {
                    var _a;
                    setIsKeyboardFocused(true);
                    // Keyboard focus should also drive the visual selection indicator
                    // (the blue focus glow is wired to `selectedTool`) so Tab moves the
                    // ring from piece to piece. BUT only when focus came via keyboard
                    // — a mouse-driven focus (click) is about to run the clickHandler
                    // which manages selection itself, including shift-click / ctrl-click
                    // multi-select. If we auto-select here on mouse focus we clobber
                    // any pending shift-extension before the click handler sees it.
                    if (isGroup)
                        return;
                    var target = e.currentTarget;
                    // `:focus-visible` matches only when the browser attributes focus
                    // to keyboard input (Tab, arrow keys) — mouse clicks produce focus
                    // without the pseudo-class in all modern browsers.
                    var isKeyboardFocus = typeof target.matches === 'function' && target.matches(':focus-visible');
                    if (!isKeyboardFocus)
                        return;
                    var targetId = (_a = data.groupId) !== null && _a !== void 0 ? _a : id;
                    if (!selectedToolRef.current.includes(targetId)) {
                        setSelectedTool([targetId]);
                    }
                }, onBlur: function () { return setIsKeyboardFocused(false); }, disabled: isGroup }), !isGroup && IS_TOUCH_DEVICE && (_jsx(SelectFocusOverlay, { id: id, 
                // Keep the select-overlay visible even when the piece is locked
                // so iPad VoiceOver users can still double-tap to open the menu
                // and reach the Unlock action. The menu's own lock-disabled
                // gating ensures only Unlock (and Delete, where allowed) remain
                // actionable while locked.
                hidden: !isFocused && !isKeyboardFocused, className: "numberMoneyPieceSelectBtn", title: t('text.selectPiece'), onAfterClick: function () {
                    var _a;
                    var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                    if (rect) {
                        setContextMenu({
                            x: rect.right,
                            y: rect.top,
                            manipulativeId: id,
                        });
                    }
                } })), renderPiece(), !isGroup && isKeyboardFocused && (_jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: {
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }, children: announceText }))] })));
};
export default memo(NumberAndMoneyPieceManipulative, function (prev, next) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var pd = prev.data;
    var nd = next.data;
    if (pd.animatingOut !== nd.animatingOut)
        return false;
    if (pd.center.x !== nd.center.x || pd.center.y !== nd.center.y)
        return false;
    if (pd.rotation !== nd.rotation)
        return false;
    if (pd.scale !== nd.scale)
        return false;
    if (pd.isFlipped !== nd.isFlipped)
        return false;
    if (pd.denomination !== nd.denomination)
        return false;
    if (pd.pieceType !== nd.pieceType)
        return false;
    if (pd.color !== nd.color)
        return false;
    if (pd.groupId !== nd.groupId)
        return false;
    if (pd.groupScale !== nd.groupScale)
        return false;
    if (pd.beforeGroupScale !== nd.beforeGroupScale)
        return false;
    if (((_a = pd.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== ((_b = nd.defaultDimensions) === null || _b === void 0 ? void 0 : _b.width))
        return false;
    if (((_c = pd.defaultDimensions) === null || _c === void 0 ? void 0 : _c.height) !== ((_d = nd.defaultDimensions) === null || _d === void 0 ? void 0 : _d.height))
        return false;
    if (((_e = pd.lock) === null || _e === void 0 ? void 0 : _e.positioning) !== ((_f = nd.lock) === null || _f === void 0 ? void 0 : _f.positioning))
        return false;
    if (((_g = pd.lock) === null || _g === void 0 ? void 0 : _g.editing) !== ((_h = nd.lock) === null || _h === void 0 ? void 0 : _h.editing))
        return false;
    // Include snappedId so both the dragged piece AND the touched target
    // re-render (picks up the nmpSnapped outline class) when they join or
    // leave a snap cluster.
    if (pd.snappedId !== nd.snappedId)
        return false;
    if (pd.animation !== nd.animation)
        return false;
    if (prev.isGroup !== next.isGroup)
        return false;
    return true;
});
